import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  Loader2, Plus, Trash2, Save, Database, ChevronDown, ChevronRight,
  Table2, GripVertical, Download, FileJson, FileCode, FileText, Layers,
  AlertTriangle, CheckCircle2, XCircle, Key, Link2, MoreVertical, Check, Wand2,
  Pencil, ChevronUp, X, ListPlus, RefreshCw, WifiOff, Terminal, DatabaseZap, Sparkles,
  KeyRound,
} from 'lucide-react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { getTables, getTableInfo, getTableUsage, saveTable, getTableSyncStatus, generateDatabaseDesign, applyIndexChange, getDatabaseDataTypes, adoptLiveTypes, adoptDbColumns, type SyncTarget, type DataTypeInfo, type IndexMethodInfo } from '@/api/designers'
import { getResolvedPreviewDb, type ResolvedPreviewDb, type ProjectDbType } from '@/api/projectDb'
import { useProjectStore } from '@/store/projectStore'
import Modal from '@/components/common/Modal'
import ConnectionsPanel, { type DialectMigration } from './database/ConnectionsPanel'
import SyncPanel from './database/SyncPanel'
import SyncTablesModal from './database/SyncTablesModal'
import OfflinePanel from './database/OfflinePanel'
import VectorSearchPanel from './database/VectorSearchPanel'
import DataBrowserView from './database/DataBrowserView'
import LiveStructureView from './database/LiveStructureView'
import SqlConsoleView from './database/SqlConsoleView'
import { useLiveCatalog } from './database/useLiveCatalog'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { ReadOnlyBadge } from '@/auth/ReadOnlyBadge'
import { useRunOnlyRedirect } from '@/auth/useRunOnlyRedirect'
import type { TableInfo, TableColumn, TableIndex } from '@/types'

// Accent colour per database engine for the prominent connection bar.
const DB_TYPE_COLOR: Record<ProjectDbType, string> = {
  postgres: '#336791',
  oracle: '#C74634',
  sqlite: '#0f80cc',
}
const DB_TYPE_LABEL: Record<ProjectDbType, string> = {
  postgres: 'Postgres',
  oracle: 'Oracle',
  sqlite: 'SQLite',
}

// Fallback until the engine-aware list loads from the backend type registry
// (GET /designers/database/data-types/{project}).
const FALLBACK_DATA_TYPES: DataTypeInfo[] = [
  'VARCHAR', 'CHAR', 'TEXT', 'RICHTEXT',
  'INTEGER', 'BIGINT', 'SMALLINT', 'NUMERIC',
  'DOUBLE', 'REAL',
  'BOOLEAN',
  'DATE', 'TIMESTAMP', 'TIMESTAMPTZ', 'TIME', 'INTERVAL',
  'JSON', 'JSONB', 'UUID', 'BYTEA', 'VECTOR',
].map((name) => ({
  name,
  size_kind: (name === 'VARCHAR' || name === 'CHAR' || name === 'VECTOR') ? 'length' as const
    : name === 'NUMERIC' ? 'precision_scale' as const : 'none' as const,
  category: 'string',
  renders_as: name,
  aliases: [],
}))

// Resolve a design value (possibly a legacy alias like NUMBER/DATETIME/CLOB)
// to its registry entry, so size handling works for legacy designs too.
function findTypeInfo(types: DataTypeInfo[], value: string): DataTypeInfo | undefined {
  const v = (value || '').toUpperCase()
  return types.find((t) => t.name === v) || types.find((t) => t.aliases.includes(v))
}

// A remembered boolean UI preference. Absent/unreadable storage falls back to
// the caller's default rather than failing the render.
function readStoredFlag(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key)
    return v === null ? fallback : v === 'true'
  } catch {
    return fallback
  }
}

// Row counts sit in a 260px rail — "1.2M" fits where "1,238,904" does not.
function compactCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`
  return String(n)
}

/* ------------------------------------------------------------------ */
/*  Design | Data tabs for the selected table                         */
/* ------------------------------------------------------------------ */
/**
 * Two views of the SAME table: what it was designed to be, and what it
 * actually holds. A DB-only table has no design, so it is rendered with the
 * `data` tab alone rather than a disabled Design tab that explains nothing.
 */
function TableTabs({ tabs, active, onChange }: {
  tabs: Array<'design' | 'data'>
  active: 'design' | 'data'
  onChange: (tab: 'design' | 'data') => void
}) {
  const LABEL = { design: 'Design', data: 'Data' } as const
  const ICON = { design: Pencil, data: Table2 } as const
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0,
      padding: '0 1.25rem', borderBottom: '1px solid var(--color-border)',
      background: 'var(--color-bg)',
    }}>
      {tabs.map((t) => {
        const Icon = ICON[t]
        const on = active === t
        return (
          <button
            key={t}
            onClick={() => onChange(t)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '0.5rem 0.75rem', fontSize: '0.875rem',
              fontWeight: on ? 600 : 500, background: 'transparent',
              border: 'none', cursor: 'pointer',
              color: on ? 'var(--color-accent)' : 'var(--color-text-secondary)',
              borderBottom: `2px solid ${on ? 'var(--color-accent)' : 'transparent'}`,
              marginBottom: -1,
            }}
          >
            <Icon size={13} />
            {LABEL[t]}
          </button>
        )
      })}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Rail column list — draggable columns under an expanded table      */
/* ------------------------------------------------------------------ */
/**
 * Columns are draggable into the SQL editor as `table.column`, and clickable
 * as a keyboard-reachable equivalent (drag-and-drop alone would make this
 * unusable without a pointer).
 */
function RailColumns({ columns, table, onDragText, onInsert }: {
  columns: Array<{ column: string; type: string; pk: boolean }> | undefined
  table: string
  onDragText: (e: React.DragEvent, text: string) => void
  onInsert: (text: string) => void
}) {
  if (columns === undefined) {
    return (
      <div style={{ padding: '2px 0 4px 26px' }}>
        <Loader2 size={11} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-text-secondary)' }} />
      </div>
    )
  }
  if (columns.length === 0) {
    return (
      <div style={{
        padding: '2px 0 4px 26px', fontSize: '0.8125rem',
        color: 'var(--color-text-secondary)',
      }}>
        Not in the live database
      </div>
    )
  }
  return (
    <div style={{ paddingBottom: 4 }}>
      {columns.map((c) => (
        <button
          key={c.column}
          draggable
          onDragStart={(e) => onDragText(e, `${table}.${c.column}`)}
          onClick={() => onInsert(`${table}.${c.column}`)}
          title={`${c.type}${c.pk ? ' · primary key' : ''} — drag or click to insert`}
          style={{
            display: 'flex', alignItems: 'center', gap: 4, width: '100%',
            padding: '2px 6px 2px 26px', fontSize: '0.8125rem', textAlign: 'left',
            background: 'transparent', border: 'none', cursor: 'grab',
            color: 'var(--color-text-secondary)',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-secondary)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          {c.pk
            ? <KeyRound size={10} style={{ color: '#f59e0b', flexShrink: 0 }} />
            : <span style={{ width: 10, flexShrink: 0 }} />}
          <span className="truncate" style={{ flex: 1 }}>{c.column}</span>
          <span style={{ fontSize: '0.8125rem', opacity: 0.55, flexShrink: 0 }}>
            {c.type.replace(/\(.*\)/, '')}
          </span>
        </button>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Popover: small absolute-positioned editor triggered by icon click */
/* ------------------------------------------------------------------ */
function Popover({
  anchorRef,
  onClose,
  children,
}: {
  anchorRef: React.RefObject<HTMLElement | null>
  onClose: () => void
  children: React.ReactNode
}) {
  const popRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    const handleClick = (e: MouseEvent) => {
      if (
        popRef.current &&
        !popRef.current.contains(e.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target as Node)
      ) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleKey)
    document.addEventListener('mousedown', handleClick)
    return () => {
      document.removeEventListener('keydown', handleKey)
      document.removeEventListener('mousedown', handleClick)
    }
  }, [onClose, anchorRef])

  // Position below the anchor, clamped to viewport
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  useEffect(() => {
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect()
      const popWidth = 220 // matches minWidth
      const popHeight = 120 // approximate max height
      let top = rect.bottom + 4
      let left = rect.left
      // Clamp to right edge
      if (left + popWidth > window.innerWidth - 8) {
        left = window.innerWidth - popWidth - 8
      }
      // Clamp to left edge
      if (left < 8) left = 8
      // If not enough space below, position above
      if (top + popHeight > window.innerHeight - 8) {
        top = rect.top - popHeight - 4
      }
      setPos({ top, left })
    }
  }, [anchorRef])

  if (!pos) return null

  return (
    <div
      ref={popRef}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        zIndex: 1000,
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
        padding: '0.5rem',
        minWidth: 220,
      }}
    >
      {children}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Index editor — add/edit a secondary index with column dropdowns   */
/* ------------------------------------------------------------------ */
const INDEX_NAME_RE = /^[A-Za-z_][A-Za-z0-9_]*$/

// Auto-suggested name: ix_<table>_<col1>_<col2>, sanitized + capped at 63
// chars (Postgres identifier limit).
function suggestIndexName(tableName: string, columns: string[]): string {
  const raw = ['ix', tableName, ...columns].join('_').toLowerCase()
  const clean = raw.replace(/[^a-z0-9_]+/g, '_').replace(/_+/g, '_')
  return clean.slice(0, 63).replace(/_+$/, '')
}

function IndexEditorModal({
  open,
  onClose,
  tableName,
  availableColumns,
  columnTypes,
  methods,
  otherIndexNames,
  initial,
  onSubmit,
  saving,
  targetLabel,
}: {
  open: boolean
  onClose: () => void
  tableName: string
  availableColumns: string[]
  // {column -> declared design type}, so a vector method can be offered on a
  // VECTOR column and refused on the others.
  columnTypes: Record<string, string>
  methods: IndexMethodInfo[]
  otherIndexNames: string[] // lowercased names of the other indexes (dup check)
  initial: TableIndex | null // null = add mode
  onSubmit: (def: TableIndex) => void
  saving: boolean
  targetLabel: string
}) {
  const [name, setName] = useState('')
  // Once the user types a name we stop auto-suggesting. Editing an existing
  // index starts "touched" so we never silently rename it.
  const [nameTouched, setNameTouched] = useState(false)
  const [cols, setCols] = useState<string[]>([''])
  const [unique, setUnique] = useState(false)
  const [method, setMethod] = useState('btree')
  const [opclasses, setOpclasses] = useState<string[]>([])

  useEffect(() => {
    if (!open) return
    setName(initial?.name || '')
    setNameTouched(!!initial)
    setCols(initial?.columns?.length ? [...initial.columns] : [''])
    setUnique(!!initial?.unique)
    setMethod(initial?.method || 'btree')
    setOpclasses(initial?.opclasses ? [...initial.opclasses] : [])
  }, [open, initial])

  useEffect(() => {
    if (nameTouched) return
    const chosen = cols.filter(Boolean)
    setName(chosen.length ? suggestIndexName(tableName, chosen) : '')
  }, [cols, nameTouched, tableName])

  const chosen = cols.filter(Boolean)
  const methodSpec = methods.find((m) => m.name === method)
  const isVectorMethod = method === 'hnsw' || method === 'ivfflat'
  const isVectorColumn = (c: string) =>
    /^vector\b/i.test((columnTypes[c] || columnTypes[c?.toUpperCase()] || '').trim())
  const nonVectorCols = isVectorMethod ? chosen.filter((c) => !isVectorColumn(c)) : []
  const vectorColumns = availableColumns.filter(isVectorColumn)
  const opclassOptions = methodSpec?.opclasses || []

  const moveCol = (i: number, dir: -1 | 1) => {
    const j = i + dir
    if (j < 0 || j >= cols.length) return
    const next = [...cols]
    ;[next[i], next[j]] = [next[j], next[i]]
    setCols(next)
  }

  const handleSubmit = () => {
    if (chosen.length === 0) {
      toast.error('Select at least one column')
      return
    }
    const trimmed = name.trim()
    if (!trimmed) {
      toast.error('Index name is required')
      return
    }
    if (!INDEX_NAME_RE.test(trimmed)) {
      toast.error('Index name may only contain letters, digits and underscores')
      return
    }
    if (trimmed.length > 63) {
      toast.error('Index name must be 63 characters or fewer')
      return
    }
    if (otherIndexNames.includes(trimmed.toLowerCase())) {
      toast.error('An index with this name already exists')
      return
    }
    if (isVectorMethod && nonVectorCols.length > 0) {
      toast.error(
        `${method} indexes an embedding — ${nonVectorCols.join(', ')} is not a VECTOR column`
      )
      return
    }
    const ops = chosen.map((_, i) => opclasses[i] || '')
    onSubmit({
      name: trimmed,
      columns: chosen,
      unique,
      ...(method && method !== 'btree' ? { method } : {}),
      ...(ops.some(Boolean) ? { opclasses: ops } : {}),
    })
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial ? `Edit Index — ${tableName}` : `Add Index — ${tableName}`}
      footer={
        <>
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={saving}>
            {saving ? (
              <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <Save size={14} />
            )}
            {saving ? 'Applying...' : `Save & apply to ${targetLabel}`}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
        {/* Columns */}
        <div>
          <div style={{
            fontSize: '0.8125rem', fontWeight: 600,
            color: 'var(--color-text-secondary)', marginBottom: 6,
          }}>
            Indexed columns <span style={{ fontWeight: 400 }}>(order matters for composite indexes)</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {cols.map((c, i) => {
              // Each dropdown offers the columns not used by the other rows
              // (its own current value stays selectable).
              const usedElsewhere = new Set(cols.filter((_, j) => j !== i).map((x) => x.toLowerCase()))
              const options = availableColumns.filter(
                (col) => col.toLowerCase() === c.toLowerCase() || !usedElsewhere.has(col.toLowerCase())
              )
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{
                    fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
                    width: 16, textAlign: 'right', flexShrink: 0,
                  }}>{i + 1}.</span>
                  <select
                    value={c}
                    onChange={(e) => {
                      const next = [...cols]
                      next[i] = e.target.value
                      setCols(next)
                    }}
                    style={{ flex: 1, padding: '0.3rem 0.375rem', fontSize: '0.875rem' }}
                  >
                    <option value="">Select column...</option>
                    {options.map((col) => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                  {opclassOptions.length > 0 && (
                    <select
                      value={opclasses[i] || ''}
                      onChange={(e) => {
                        const next = [...opclasses]
                        next[i] = e.target.value
                        setOpclasses(next)
                      }}
                      title="Operator class — which operators this index can answer for the column"
                      style={{ width: 150, padding: '0.3rem 0.375rem', fontSize: '0.8125rem' }}
                    >
                      {opclassOptions.map((op) => (
                        <option key={op || 'default'} value={op}>
                          {op || 'default ops'}
                        </option>
                      ))}
                    </select>
                  )}
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => moveCol(i, -1)}
                    disabled={i === 0}
                    title="Move up"
                    style={{ padding: 2, opacity: i === 0 ? 0.3 : 1 }}
                  >
                    <ChevronUp size={13} />
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => moveCol(i, 1)}
                    disabled={i === cols.length - 1}
                    title="Move down"
                    style={{ padding: 2, opacity: i === cols.length - 1 ? 0.3 : 1 }}
                  >
                    <ChevronDown size={13} />
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setCols(cols.length > 1 ? cols.filter((_, j) => j !== i) : [''])}
                    title="Remove column"
                    style={{ padding: 2 }}
                  >
                    <X size={13} style={{ color: 'var(--color-error)' }} />
                  </button>
                </div>
              )
            })}
          </div>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setCols([...cols, ''])}
            disabled={chosen.length >= availableColumns.length || cols.some((c) => !c)}
            style={{ marginTop: 8 }}
          >
            <Plus size={13} />
            Add column
          </button>
        </div>

        {/* Name (auto-suggested from table + columns) */}
        <div>
          <div style={{
            fontSize: '0.8125rem', fontWeight: 600,
            color: 'var(--color-text-secondary)', marginBottom: 6,
          }}>
            Index name
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setNameTouched(true) }}
              placeholder={chosen.length ? suggestIndexName(tableName, chosen) : 'ix_table_column'}
              style={{ flex: 1, padding: '0.3rem 0.375rem', fontSize: '0.875rem', fontFamily: 'monospace' }}
            />
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => { setNameTouched(false); setName(chosen.length ? suggestIndexName(tableName, chosen) : '') }}
              title="Regenerate suggested name"
              style={{ padding: 4 }}
            >
              <Wand2 size={13} />
            </button>
          </div>
          {!nameTouched && chosen.length > 0 && (
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4 }}>
              Auto-suggested — updates as you pick columns
            </div>
          )}
        </div>

        {/* Access method — what KIND of index this is */}
        {methods.length > 1 && (
          <div>
            <div style={{
              fontSize: '0.8125rem', fontWeight: 600,
              color: 'var(--color-text-secondary)', marginBottom: 6,
            }}>
              Index method
            </div>
            <select
              value={method}
              onChange={(e) => {
                const next = e.target.value
                setMethod(next)
                // The operator classes belong to the method — carrying the old
                // method's over would build an index the DB rejects.
                const spec = methods.find((m) => m.name === next)
                setOpclasses(spec?.opclasses?.length ? cols.map(() => spec.opclasses[0]) : [])
                if (next !== 'btree') setUnique(false)
              }}
              style={{ width: '100%', padding: '0.3rem 0.375rem', fontSize: '0.875rem' }}
            >
              {methods.map((m) => (
                <option key={m.name} value={m.name}>{m.label}</option>
              ))}
            </select>
            {methodSpec?.note && (
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4, lineHeight: 1.5 }}>
                {methodSpec.note}
              </div>
            )}
            {isVectorMethod && vectorColumns.length === 0 && (
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-warning, #d97706)', marginTop: 4, lineHeight: 1.5 }}>
                This table has no VECTOR column. A vector index needs one —
                add a column of type VECTOR (its size is the embedding width,
                e.g. 1536) and populate it before indexing.
              </div>
            )}
            {isVectorMethod && nonVectorCols.length > 0 && (
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-error)', marginTop: 4, lineHeight: 1.5 }}>
                {nonVectorCols.join(', ')} is not a VECTOR column — pick{' '}
                {vectorColumns.length ? vectorColumns.join(' or ') : 'a vector column'} instead.
              </div>
            )}
          </div>
        )}

        {/* Unique */}
        <label style={{
          display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.875rem',
          cursor: methodSpec && !methodSpec.supports_unique ? 'not-allowed' : 'pointer',
          opacity: methodSpec && !methodSpec.supports_unique ? 0.55 : 1,
        }}>
          <input
            type="checkbox"
            checked={unique}
            disabled={!!methodSpec && !methodSpec.supports_unique}
            onChange={(e) => setUnique(e.target.checked)}
          />
          Unique index{' '}
          <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.8125rem' }}>
            {methodSpec && !methodSpec.supports_unique
              ? `(only a b-tree can enforce uniqueness)`
              : '(rejects duplicate values)'}
          </span>
        </label>
      </div>
    </Modal>
  )
}

/* ------------------------------------------------------------------ */
/*  SortableRow — wraps each <tr> for drag-and-drop reordering        */
/* ------------------------------------------------------------------ */
interface SortableRowProps {
  id: string
  idx: number
  col: TableColumn
  syncStatus: TableSyncStatus | null
  dataTypes: DataTypeInfo[]
  updateColumn: (index: number, field: keyof TableColumn, value: unknown) => void
  removeColumn: (index: number) => void
  fkEditIdx: number | null
  setFkEditIdx: (v: number | null) => void
  descEditIdx: number | null
  setDescEditIdx: (v: number | null) => void
  onAdoptLiveTypes?: () => void
}

interface ColumnSyncInfo {
  column_name: string
  design_type: string | null
  db_type: string | null
  // 'external_type_drift' = adopted (managed:false) table whose design type
  // differs from the live DB — a design-repair situation ("Adopt live types"),
  // never a deploy problem.
  status: 'synced' | 'missing' | 'type_mismatch' | 'extra_in_db' | 'external_type_drift'
  is_pk_in_design: boolean
  is_pk_in_db: boolean
  // The registry's best design representation of the live type (present on
  // non-synced columns) — what "Adopt live types" writes into the design.
  live_canonical?: { data_type: string; size: string; native_type: string }
}
interface IndexSyncInfo {
  name: string
  columns: string[]
  unique: boolean
  /** Designed access method ('btree' when unset). */
  method?: string
  /** Access method the DB actually built it with, when it exists there. */
  db_method?: string | null
  // 'synced' = present in DB, 'missing' = designed but not in DB,
  // 'method_differs' = same columns in the DB but built with another access
  // method (a b-tree where the design asks for hnsw answers different
  // queries), 'extra_in_db' = exists in DB but not in the design.
  status: 'synced' | 'missing' | 'method_differs' | 'extra_in_db'
  db_name: string | null
}
interface TableSyncStatus {
  table_exists: boolean
  // false = adopted/externally-managed table (live DB owns the structure)
  managed?: boolean
  engine?: string
  columns: ColumnSyncInfo[]
  primary_key_in_db: string[]
  primary_key_in_design: string[]
  pk_exists_in_db: boolean
  indexes?: IndexSyncInfo[]
}

function SortableRow({
  id,
  idx,
  col,
  syncStatus,
  dataTypes,
  updateColumn,
  removeColumn,
  fkEditIdx,
  setFkEditIdx,
  descEditIdx,
  setDescEditIdx,
  onAdoptLiveTypes,
}: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const fkBtnRef = useRef<HTMLButtonElement>(null)
  const descBtnRef = useRef<HTMLButtonElement>(null)

  // Resolve sync info for this column
  const colSync = syncStatus?.columns.find(
    (c) => c.column_name.toUpperCase() === (col.column_name || '').toUpperCase()
  )

  const hasFk = !!col.foreign_key
  const hasDesc = !!col.description

  return (
    <tr ref={setNodeRef} style={style}>
      {/* # sequence */}
      <td style={{ padding: '0.25rem 0.125rem', textAlign: 'center' }}>
        <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', opacity: 0.6 }}>
          {idx + 1}
        </span>
      </td>
      {/* Drag handle */}
      <td style={{ padding: '0.25rem 0', textAlign: 'center', cursor: 'grab' }}>
        <GripVertical
          size={12}
          style={{ color: 'var(--color-text-secondary)', opacity: 0.5 }}
          {...listeners}
          {...attributes}
        />
      </td>
      {/* Column Name */}
      <td style={{ padding: '0.25rem 0.375rem' }}>
        <input
          type="text"
          value={col.column_name}
          onChange={(e) => updateColumn(idx, 'column_name', e.target.value)}
          style={{ padding: '0.25rem 0.375rem', fontSize: '0.875rem', width: '100%' }}
          placeholder="column_name"
        />
      </td>
      {/* Data Type */}
      <td style={{ padding: '0.25rem 0.25rem' }}>
        {(() => {
          const currentUpper = (col.data_type || '').toUpperCase()
          const typeInfo = findTypeInfo(dataTypes, currentUpper)
          // Legacy/native values not in the engine list stay selectable.
          const options = dataTypes.map((t) => t.name)
          if (currentUpper && !options.includes(currentUpper)) options.push(currentUpper)
          return (
            <>
              <select
                value={currentUpper}
                onChange={(e) => updateColumn(idx, 'data_type', e.target.value)}
                style={{ padding: '0.25rem 0.125rem', fontSize: '0.875rem', width: '100%' }}
              >
                {options.map((dt) => (
                  <option key={dt} value={dt}>{dt}</option>
                ))}
              </select>
              {col.native_type && (
                <div
                  title="The external database's declared type — used verbatim for this adopted column"
                  style={{
                    fontSize: '0.8125rem', color: '#3b82f6', marginTop: 1,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}
                >
                  native: {col.native_type}
                </div>
              )}
              {!typeInfo && currentUpper && !col.native_type && null}
            </>
          )
        })()}
      </td>
      {/* Size */}
      <td style={{ padding: '0.25rem 0.25rem' }}>
        {(() => {
          const typeInfo = findTypeInfo(dataTypes, col.data_type)
          const sizeless = typeInfo?.size_kind === 'none'
          return (
            <input
              type="text"
              value={sizeless ? '' : (col.size || '')}
              onChange={(e) => updateColumn(idx, 'size', e.target.value)}
              disabled={sizeless}
              style={{
                padding: '0.25rem 0.25rem', fontSize: '0.875rem', width: '100%',
                opacity: sizeless ? 0.4 : 1,
              }}
              placeholder={sizeless ? '—'
                : typeInfo?.size_kind === 'precision_scale' ? '10,2'
                : typeInfo?.name === 'VECTOR' ? '1536' : '255'}
              title={sizeless
                ? `${(col.data_type || '').toUpperCase()} takes no size`
                : typeInfo?.name === 'VECTOR'
                  // For a vector the "size" IS the embedding width, and it has
                  // to match the model that produced the vectors — two widths
                  // are not comparable at all.
                  ? 'Embedding dimensions — must match the model that produces the vectors (1536 for OpenAI text-embedding-3-small)'
                  : undefined}
            />
          )
        })()}
      </td>
      {/* Null */}
      <td style={{ padding: '0.25rem', textAlign: 'center' }}>
        <input
          type="checkbox"
          checked={col.nullable ?? true}
          onChange={(e) => updateColumn(idx, 'nullable', e.target.checked)}
        />
      </td>
      {/* PK */}
      <td style={{ padding: '0.25rem', textAlign: 'center' }}>
        <input
          type="checkbox"
          checked={col.primary_key ?? false}
          onChange={(e) => updateColumn(idx, 'primary_key', e.target.checked)}
        />
      </td>
      {/* Icons: FK, Desc, Status */}
      <td style={{ padding: '0.25rem 0.125rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center' }}>
          {/* FK icon */}
          <button
            ref={fkBtnRef}
            onClick={() => setFkEditIdx(fkEditIdx === idx ? null : idx)}
            title={hasFk ? `FK: ${col.foreign_key}` : 'Set foreign key'}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 2,
              color: hasFk ? 'var(--color-accent)' : 'var(--color-text-secondary)',
              opacity: hasFk ? 1 : 0.4,
              display: 'flex', alignItems: 'center',
            }}
          >
            <Link2 size={13} />
          </button>
          {fkEditIdx === idx && (
            <Popover anchorRef={fkBtnRef} onClose={() => setFkEditIdx(null)}>
              <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 4 }}>
                Foreign Key
              </div>
              <input
                type="text"
                value={col.foreign_key || ''}
                onChange={(e) => updateColumn(idx, 'foreign_key', e.target.value)}
                placeholder="table.column"
                autoFocus
                style={{ fontSize: '0.875rem', width: '100%', padding: '0.25rem 0.375rem' }}
              />
            </Popover>
          )}

          {/* Desc icon */}
          <button
            ref={descBtnRef}
            onClick={() => setDescEditIdx(descEditIdx === idx ? null : idx)}
            title={hasDesc ? col.description : 'Add description'}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 2,
              color: hasDesc ? 'var(--color-accent)' : 'var(--color-text-secondary)',
              opacity: hasDesc ? 1 : 0.4,
              display: 'flex', alignItems: 'center',
            }}
          >
            <FileText size={13} />
          </button>
          {descEditIdx === idx && (
            <Popover anchorRef={descBtnRef} onClose={() => setDescEditIdx(null)}>
              <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 4 }}>
                Description
              </div>
              <textarea
                value={col.description || ''}
                onChange={(e) => updateColumn(idx, 'description', e.target.value)}
                placeholder="Column description..."
                autoFocus
                rows={3}
                style={{ fontSize: '0.875rem', width: '100%', padding: '0.25rem 0.375rem', resize: 'vertical' }}
              />
            </Popover>
          )}

          {/* DB Status icon */}
          {syncStatus && (() => {
            if (!syncStatus.table_exists) {
              return <span title="Table not in DB"><XCircle size={13} style={{ color: '#ef4444' }} /></span>
            }
            if (!colSync || colSync.status === 'missing') {
              return <span title="Not in DB"><AlertTriangle size={13} style={{ color: '#f59e0b' }} /></span>
            }
            if (colSync.status === 'external_type_drift') {
              return (
                <button
                  onClick={onAdoptLiveTypes}
                  title={`Live DB type is ${colSync.db_type} — click to adopt the live types into the design (and bound transaction models)`}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                    display: 'flex', alignItems: 'center',
                  }}
                >
                  <RefreshCw size={13} style={{ color: '#3b82f6' }} />
                </button>
              )
            }
            if (colSync.status === 'type_mismatch') {
              return <span title={`Type mismatch: ${colSync.db_type}`}><AlertTriangle size={13} style={{ color: '#f59e0b' }} /></span>
            }
            return <span title="Synced"><CheckCircle2 size={13} style={{ color: '#22c55e' }} /></span>
          })()}
        </div>
      </td>
      {/* Delete */}
      <td style={{ padding: '0.25rem', textAlign: 'center' }}>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => removeColumn(idx)}
          title="Remove column"
          style={{ padding: 2 }}
        >
          <Trash2 size={13} style={{ color: 'var(--color-error)' }} />
        </button>
      </td>
    </tr>
  )
}

/* ================================================================== */
/*  Main page component                                               */
/* ================================================================== */
export default function DatabaseDesignerPage() {
  const { project } = useParams<{ project: string }>()
  useRunOnlyRedirect(project)
  const projectName = project || 'Draft Application'
  const { currentProject, selectProject } = useProjectStore()

  // Restore currentProject in store from URL param on refresh
  useEffect(() => {
    if (project && currentProject?.application.folder_name !== project) {
      selectProject(project)
    }
  }, [project, currentProject, selectProject])

  const [tables, setTables] = useState<Array<{ name: string; description: string }>>([])
  const [selectedTable, setSelectedTable] = useState<string | null>(null)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [tableInfo, setTableInfo] = useState<TableInfo | null>(null)
  const [tableUsage, setTableUsage] = useState<{ table_name: string; objects: unknown[] } | null>(null)
  const [usageExpanded, setUsageExpanded] = useState(false)
  const [loadingTables, setLoadingTables] = useState(true)
  const [loadingTable, setLoadingTable] = useState(false)
  const [saving, setSaving] = useState(false)
  const { canEditObjectsOrLoading: canEdit } = useProjectPermissions()
  const [newTableName, setNewTableName] = useState('')
  const [showAddTable, setShowAddTable] = useState(false)
  // Per-project database connections (preview/run override + sync targets).
  const [showConnections, setShowConnections] = useState(false)
  // External-database sync mappings (push/pull with an external app DB).
  const [showSync, setShowSync] = useState(false)
  const [showOffline, setShowOffline] = useState(false)
  const [showVectorSearch, setShowVectorSearch] = useState(false)
  // The database preview/run currently resolves to — shown prominently in
  // the connection bar so it's always clear where this project deploys.
  const [resolvedDb, setResolvedDb] = useState<ResolvedPreviewDb | null>(null)
  // "Sync project tables" modal — opened after a DB change or manually.
  const [showSyncTables, setShowSyncTables] = useState(false)
  const [syncIntro, setSyncIntro] = useState<string | undefined>(undefined)

  const refreshResolvedDb = useCallback(async () => {
    try {
      setResolvedDb(await getResolvedPreviewDb(projectName))
    } catch {
      setResolvedDb(null)
    }
  }, [projectName])

  useEffect(() => {
    void refreshResolvedDb()
  }, [refreshResolvedDb])

  // The preview/run database changed in the Connections panel → refresh the
  // bar and offer to sync the project's tables to the new database.
  const handlePreviewDbChanged = useCallback((summary: string) => {
    void refreshResolvedDb()
    setShowConnections(false)
    setSyncIntro(`Database changed to ${summary}. Sync the project's tables to the new database now so it has the schema.`)
    setShowSyncTables(true)
  }, [refreshResolvedDb])

  // The preview/run database was switched to a DIFFERENT engine AND the
  // project has generated business logic written for the old engine. Hand the
  // migration off to the Ask-AI agent: it plans and executes the translation
  // of the logic + the table rebuild on the new database (so we do NOT open
  // the plain table-sync flow here — the agent rebuilds the tables itself).
  const handleDialectMigration = useCallback((m: DialectMigration) => {
    void refreshResolvedDb()
    setShowConnections(false)
    const fromL = DB_TYPE_LABEL[m.from]
    const toL = DB_TYPE_LABEL[m.to]
    const fnWord = m.functionCount === 1 ? 'function' : 'functions'
    const objList = m.objects.length ? ` (objects: ${m.objects.join(', ')})` : ''
    const prompt =
      `The project's Preview & Run database has been switched from ${fromL} to `
      + `${toL}. Migrate the application to ${toL}.\n\n`
      + `It has ${m.functionCount} generated business-logic ${fnWord} written in `
      + `${m.languages.join(', ')} for ${fromL}${objList}. Make a step-by-step `
      + `migration plan and then execute it:\n`
      + `1. Translate every business-logic function to ${toL} (its natural `
      + `business-logic language), fixing any dialect-specific SQL.\n`
      + `2. Update any dialect-specific SQL in reports, dashboards and general `
      + `processes for ${toL}.\n`
      + `3. Rebuild/sync all transaction tables on the new ${toL} database and `
      + `deploy the migrated functions.\n`
      + `4. Verify the objects compile and run on ${toL}.`
    toast(`Starting Ask-AI ${fromL} → ${toL} migration…`, { icon: '🛠️', duration: 5000 })
    window.dispatchEvent(new CustomEvent('twasta:agent-run-task', {
      detail: { project: projectName, prompt },
    }))
  }, [refreshResolvedDb, projectName])
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [exportingAll, setExportingAll] = useState(false)
  const [generatingDesign, setGeneratingDesign] = useState(false)

  // Popover state
  const [fkEditIdx, setFkEditIdx] = useState<number | null>(null)
  const [descEditIdx, setDescEditIdx] = useState<number | null>(null)

  // Index editor state
  const [indexModalOpen, setIndexModalOpen] = useState(false)
  const [editingIndex, setEditingIndex] = useState<TableIndex | null>(null) // null = add mode
  const [applyingIndex, setApplyingIndex] = useState(false)

  // DB sync status
  const [syncStatus, setSyncStatus] = useState<TableSyncStatus | null>(null)
  const [syncLoading, setSyncLoading] = useState(false)
  const [tableSyncSummary, setTableSyncSummary] = useState<Record<string, 'synced' | 'mismatch' | 'external_drift' | 'not_in_db' | 'unknown'>>({})

  // Engine-aware data types for the type dropdown (shared type registry).
  const [dataTypes, setDataTypes] = useState<DataTypeInfo[]>(FALLBACK_DATA_TYPES)
  // Access methods this project's engine can build. Empty until loaded, which
  // leaves the index editor on the b-tree it has always used.
  const [indexMethods, setIndexMethods] = useState<IndexMethodInfo[]>([])
  useEffect(() => {
    getDatabaseDataTypes(projectName)
      .then((res) => {
        if (res.success && res.types?.length) setDataTypes(res.types)
        if (res.index_methods?.length) setIndexMethods(res.index_methods)
      })
      .catch(() => { /* fallback list stays */ })
  }, [projectName])

  // "Adopt live types" — repair adopted designs (and bound transaction
  // models) from the live DB. tbl omitted = every adopted table.
  const [adoptingTypes, setAdoptingTypes] = useState(false)

  // Verification target: which DB the sync status is computed against.
  // Defaults to live preview; persisted per-browser.
  const [syncTarget, setSyncTarget] = useState<SyncTarget>(() => {
    try {
      const v = localStorage.getItem('db.syncTarget')
      return v === 'deployment' ? 'deployment' : 'preview'
    } catch {
      return 'preview'
    }
  })
  const [syncMenuOpen, setSyncMenuOpen] = useState(false)
  const syncMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    try { localStorage.setItem('db.syncTarget', syncTarget) } catch { /* ignore */ }
  }, [syncTarget])

  useEffect(() => {
    if (!syncMenuOpen) return
    const onDown = (e: MouseEvent) => {
      if (syncMenuRef.current && !syncMenuRef.current.contains(e.target as Node)) {
        setSyncMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [syncMenuOpen])

  /* ────────────────────────────────────────────────────────────────
     Live database browsing — the Data tab and the SQL console.
     `railSelection` is what the left rail has selected: the pinned SQL
     Query entry (parent-level, always reachable) or a table. A table's
     right pane then has Design | Data tabs — Design is the design editor
     below, Data reads the live table. A DB-only table has no design, so
     it shows Data only.
  ──────────────────────────────────────────────────────────────── */
  const [railSelection, setRailSelection] = useState<'query' | 'table'>('table')
  const [tableTab, setTableTab] = useState<'design' | 'data'>('design')
  // A table selected from the "In database only" group — it has no design
  // file, so it is tracked separately from `selectedTable`.
  const [selectedLiveTable, setSelectedLiveTable] = useState<string | null>(null)
  const [expandedRailTable, setExpandedRailTable] = useState<string | null>(null)
  // Both rail groups collapse, and the choice is remembered per browser.
  // "In database only" starts CLOSED: it can hold hundreds of tables (213 in
  // a real project against 19 designed ones), and opening the tab into that
  // list buries the project's own tables under a scroll.
  const [projectGroupOpen, setProjectGroupOpen] = useState(
    () => readStoredFlag('db.railProjectOpen', true))
  const [dbOnlyGroupOpen, setDbOnlyGroupOpen] = useState(
    () => readStoredFlag('db.railDbOnlyOpen', false))

  useEffect(() => {
    try { localStorage.setItem('db.railProjectOpen', String(projectGroupOpen)) }
    catch { /* preference only */ }
  }, [projectGroupOpen])
  useEffect(() => {
    try { localStorage.setItem('db.railDbOnlyOpen', String(dbOnlyGroupOpen)) }
    catch { /* preference only */ }
  }, [dbOnlyGroupOpen])
  // Carries a nonce so that clicking "Query" on the SAME table twice still
  // re-seeds the editor — the action is explicit, so it should always act.
  const [querySeed, setQuerySeed] = useState<{ sql: string; n: number } | undefined>(undefined)
  // Set by SqlConsoleView so rail clicks can insert at the editor's cursor.
  const consoleInsertRef = useRef<((text: string) => void) | null>(null)

  const catalog = useLiveCatalog(projectName, syncTarget)

  const openQueryConsole = useCallback((seed?: string) => {
    setRailSelection('query')
    if (seed) setQuerySeed((prev) => ({ sql: seed, n: (prev?.n ?? 0) + 1 }))
    setMobileNavOpen(false)
  }, [])

  const selectLiveTable = useCallback((name: string) => {
    setSelectedLiveTable(name)
    setRailSelection('table')
    // Same Design | Data interface as a project table — Design just renders
    // the live structure read-only, since there is no design file to edit.
    setMobileNavOpen(false)
  }, [])

  // Dragging a table/column into the console. CodeMirror consumes a native
  // text/plain drop at the exact drop position; dnd-kit (used for column
  // reorder in the Design view) cannot target the editor that way.
  const dragText = (e: React.DragEvent, text: string) => {
    e.dataTransfer.setData('text/plain', text)
    e.dataTransfer.effectAllowed = 'copy'
  }

  const toggleRailTable = useCallback((name: string) => {
    setExpandedRailTable((cur) => {
      const next = cur === name ? null : name
      if (next) void catalog.loadColumns(next)
      return next
    })
  }, [catalog])

  // Physical tables with no Database_Design entry — the ones the tab could
  // never show before.
  const dbOnlyTables = catalog.tables.filter((t) => !t.in_project)

  // DnD sensor — require 5px drag distance to avoid accidental drags
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  )

  // Sortable IDs: use column_name or fallback to index-based id
  const sortableIds = tableInfo
    ? tableInfo.columns.map((col, i) => col.column_name || `__col_${i}`)
    : []

  const handleDragEnd = (event: DragEndEvent) => {
    if (!tableInfo) return
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = sortableIds.indexOf(active.id as string)
    const newIndex = sortableIds.indexOf(over.id as string)
    if (oldIndex === -1 || newIndex === -1) return

    const reordered = arrayMove(tableInfo.columns, oldIndex, newIndex)
    // Update sequence numbers
    const updated = reordered.map((col, i) => ({ ...col, sequence: i + 1 }))
    setTableInfo({ ...tableInfo, columns: updated })
    // Close any open popovers
    setFkEditIdx(null)
    setDescEditIdx(null)
  }

  // Load all table details for export
  const loadAllTableDetails = useCallback(async (): Promise<TableInfo[]> => {
    const allInfos: TableInfo[] = []
    const promises = tables.map(async (t) => {
      if (t.name === selectedTable && tableInfo) {
        return tableInfo
      }
      try {
        const data = await getTableInfo(projectName, t.name)
        return data.table as TableInfo
      } catch {
        return { table_name: t.name, table_description: t.description || '', columns: [] } as TableInfo
      }
    })
    const results = await Promise.all(promises)
    for (const r of results) {
      if (r) allInfos.push(r)
    }
    return allInfos
  }, [tables, selectedTable, tableInfo, projectName])

  // Load table list (reusable so actions like "Generate Design" can refresh)
  const refreshTables = useCallback(async () => {
    setLoadingTables(true)
    try {
      const data = await getTables(projectName)
      setTables(data.tables || [])
    } catch {
      toast.error('Failed to load tables')
    } finally {
      setLoadingTables(false)
    }
  }, [projectName])

  useEffect(() => {
    refreshTables()
  }, [refreshTables])

  // Generate Database_Design/*.json from existing transaction models
  const handleGenerateDesign = useCallback(async () => {
    setGeneratingDesign(true)
    try {
      const res = await generateDatabaseDesign(projectName)
      if (res?.success) {
        toast.success(res.message || 'Database design generated')
        await refreshTables()
      } else {
        toast.error(res?.message || 'Failed to generate database design')
      }
    } catch {
      toast.error('Failed to generate database design')
    } finally {
      setGeneratingDesign(false)
    }
  }, [projectName, refreshTables])

  // Load selected table
  const loadTable = useCallback(async (tableName: string) => {
    setLoadingTable(true)
    setSelectedTable(tableName)
    // Selecting a designed table leaves the DB-only selection and returns the
    // rail to table mode (it may have been on the SQL Query entry).
    setSelectedLiveTable(null)
    setRailSelection('table')
    setSyncStatus(null)
    setFkEditIdx(null)
    setDescEditIdx(null)
    try {
      const [tableData, usageData] = await Promise.all([
        getTableInfo(projectName, tableName),
        getTableUsage(projectName, tableName).catch(() => ({ usage: null })),
      ])
      // Normalize: split combined data_type like "VARCHAR(255)" into separate data_type + size
      const normalized = tableData.table
      normalized.columns = normalized.columns.map((col: TableColumn) => {
        const match = col.data_type.match(/^(\w+)\((.+)\)$/)
        if (match && !col.size) {
          return { ...col, data_type: match[1], size: match[2] }
        }
        return col
      })
      setTableInfo(normalized)
      setTableUsage(usageData.usage || null)

      // Fetch sync status in background
      setSyncLoading(true)
      getTableSyncStatus(projectName, tableName, syncTarget)
        .then((res) => {
          if (res.sync) {
            setSyncStatus(res.sync)
            updateTableSyncSummary(tableName, res.sync)
          }
        })
        .catch(() => {})
        .finally(() => setSyncLoading(false))
    } catch {
      toast.error('Failed to load table info')
    } finally {
      setLoadingTable(false)
    }
  }, [projectName, syncTarget])

  // Columns the live table has that the design lacks — the count drives the
  // "add from DB" action, which is the only way to close that gap.
  // `<col>_bidx` companions are excluded: they belong to the encryption
  // engine, the backend refuses to adopt them, and offering to add a column
  // that then gets skipped is worse than not offering at all.
  const extraDbColumnCount = (syncStatus?.columns || [])
    .filter((c) => c.status === 'extra_in_db'
      && !c.column_name.toLowerCase().endsWith('_bidx')).length
  const [adoptingDbColumns, setAdoptingDbColumns] = useState(false)

  const handleAdoptDbColumns = useCallback(async () => {
    if (!selectedTable) return
    setAdoptingDbColumns(true)
    try {
      const res = await adoptDbColumns(projectName, selectedTable, syncTarget)
      if (res.success) {
        toast.success(res.message || 'Columns added to the design')
        await loadTable(selectedTable)
      } else {
        toast.error(res.message || 'Could not add the columns')
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not add the columns')
    } finally {
      setAdoptingDbColumns(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectName, selectedTable, syncTarget, loadTable])

  // A DB-only table just became a project table: it moves rail groups, so the
  // catalog and the design list both have to be re-read, and the selection
  // has to follow it into the (now editable) design.
  const handleAdoptedTable = useCallback((designName: string) => {
    setSelectedLiveTable(null)
    setTableTab('design')
    void refreshTables()
    void catalog.refresh()
    catalog.invalidateColumns(designName)
    void loadTable(designName)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, refreshTables, loadTable])

  const updateTableSyncSummary = (tableName: string, sync: TableSyncStatus) => {
    let status: 'synced' | 'mismatch' | 'external_drift' | 'not_in_db' = 'synced'
    if (!sync.table_exists) {
      status = 'not_in_db'
    } else if (sync.managed === false) {
      // Adopted table: the live DB is the source of truth — any difference is
      // design drift (repair with "Adopt live types"), never a deploy problem.
      const hasDrift = sync.columns.some((c) => c.status !== 'synced')
      if (hasDrift) status = 'external_drift'
    } else {
      const hasMismatch = sync.columns.some(
        (c) => c.status === 'missing' || c.status === 'type_mismatch' || c.status === 'extra_in_db'
      )
      const pkMismatch = !sync.pk_exists_in_db && sync.primary_key_in_design.length > 0
      if (hasMismatch || pkMismatch) status = 'mismatch'
    }
    setTableSyncSummary((prev) => ({ ...prev, [tableName]: status }))
  }

  // Fetch sync summary for all tables on load (or when target changes)
  useEffect(() => {
    if (tables.length === 0) return
    tables.forEach((t) => {
      getTableSyncStatus(projectName, t.name, syncTarget)
        .then((res) => {
          if (res.sync) updateTableSyncSummary(t.name, res.sync)
          else setTableSyncSummary((prev) => ({ ...prev, [t.name]: 'unknown' }))
        })
        .catch(() => setTableSyncSummary((prev) => ({ ...prev, [t.name]: 'unknown' })))
    })
  }, [tables, projectName, syncTarget])

  // When the user switches verification target, refresh the currently
  // selected table's sync status so the column-level icons update too.
  useEffect(() => {
    if (!selectedTable) return
    setSyncLoading(true)
    getTableSyncStatus(projectName, selectedTable, syncTarget)
      .then((res) => {
        if (res.sync) {
          setSyncStatus(res.sync)
          updateTableSyncSummary(selectedTable, res.sync)
        } else {
          setSyncStatus(null)
        }
      })
      .catch(() => setSyncStatus(null))
      .finally(() => setSyncLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncTarget])

  const handleAddTable = useCallback(() => {
    if (!newTableName.trim()) {
      toast.error('Please enter a table name')
      return
    }
    const name = newTableName.trim().replace(/\s+/g, '_').toLowerCase()
    if (tables.some((t) => t.name === name)) {
      toast.error('Table already exists')
      return
    }
    setTables((prev) => [...prev, { name, description: '' }])
    setNewTableName('')
    setShowAddTable(false)
    setSelectedTable(name)
    setTableInfo({
      table_name: name,
      table_description: '',
      columns: [
        { column_name: 'id', data_type: 'INTEGER', primary_key: true, nullable: false, sequence: 1 },
      ],
    })
  }, [newTableName, tables])

  const updateColumn = (index: number, field: keyof TableColumn, value: unknown) => {
    if (!tableInfo) return
    const newColumns = [...tableInfo.columns]
    newColumns[index] = { ...newColumns[index], [field]: value }
    // Manually picking a type overrides the adopted native passthrough.
    if (field === 'data_type' && newColumns[index].native_type) {
      delete newColumns[index].native_type
    }
    setTableInfo({ ...tableInfo, columns: newColumns })
  }

  const addColumn = () => {
    if (!tableInfo) return
    const nextSeq = tableInfo.columns.length + 1
    setTableInfo({
      ...tableInfo,
      columns: [
        ...tableInfo.columns,
        { column_name: '', data_type: 'VARCHAR', size: '255', nullable: true, primary_key: false, sequence: nextSeq },
      ],
    })
  }

  const removeColumn = (index: number) => {
    if (!tableInfo) return
    const newColumns = tableInfo.columns.filter((_, i) => i !== index)
    setTableInfo({ ...tableInfo, columns: newColumns })
    // Close popovers if they pointed at the removed row
    if (fkEditIdx === index) setFkEditIdx(null)
    if (descEditIdx === index) setDescEditIdx(null)
  }

  const handleSave = useCallback(async () => {
    if (!tableInfo || !selectedTable) return
    setSaving(true)
    try {
      // Ensure sequence values are up-to-date before saving
      const withSeq = {
        ...tableInfo,
        columns: tableInfo.columns.map((col, i) => ({ ...col, sequence: i + 1 })),
      }
      await saveTable(projectName, selectedTable, withSeq)
      toast.success('Table saved successfully')
    } catch {
      toast.error('Failed to save table')
    } finally {
      setSaving(false)
    }
  }, [tableInfo, selectedTable, projectName])

  // ---- Index management -------------------------------------------------
  const targetLabel = syncTarget === 'preview' ? 'live preview DB' : 'deployment DB'

  // Re-fetch sync status after an index change so badges update.
  const refreshSync = useCallback((tableName: string) => {
    setSyncLoading(true)
    getTableSyncStatus(projectName, tableName, syncTarget)
      .then((res) => {
        if (res.sync) {
          setSyncStatus(res.sync)
          updateTableSyncSummary(tableName, res.sync)
        } else {
          setSyncStatus(null)
        }
      })
      .catch(() => setSyncStatus(null))
      .finally(() => setSyncLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectName, syncTarget])

  // Persist the full table design (same shape handleSave writes).
  const persistTable = useCallback(async (updated: TableInfo, table: string) => {
    const withSeq = {
      ...updated,
      columns: updated.columns.map((col, i) => ({ ...col, sequence: i + 1 })),
    }
    await saveTable(projectName, table, withSeq)
  }, [projectName])

  // Find the sync entry for a designed index (to know its DB-side name).
  const findIndexSync = (name: string): IndexSyncInfo | undefined =>
    (syncStatus?.indexes || []).find((s) => s.name.toLowerCase() === name.toLowerCase())

  // Add or edit an index: update design JSON, then apply the DDL to the
  // currently selected verification target DB.
  const handleIndexSubmit = async (def: TableIndex) => {
    if (!tableInfo || !selectedTable) return
    setApplyingIndex(true)
    try {
      const indexes = [...(tableInfo.indexes || [])]
      let action: 'create' | 'recreate' = 'create'
      let dropName: string | undefined
      if (editingIndex) {
        const i = indexes.findIndex((ix) => ix.name === editingIndex.name)
        if (i >= 0) indexes[i] = def
        else indexes.push(def)
        // If the old definition is live in the DB, replace it (drop + create).
        // 'method_differs' counts as live: the DB has an index under that name
        // built the wrong way, and CREATE alone would leave it standing.
        const oldSync = findIndexSync(editingIndex.name)
        if ((oldSync?.status === 'synced' || oldSync?.status === 'method_differs')
            && oldSync.db_name) {
          action = 'recreate'
          dropName = oldSync.db_name
        }
      } else {
        indexes.push(def)
      }
      const updated = { ...tableInfo, indexes }
      setTableInfo(updated)
      await persistTable(updated, selectedTable)

      const res = await applyIndexChange(projectName, selectedTable, {
        target: syncTarget, action,
        index: {
          name: def.name, columns: def.columns, unique: def.unique,
          method: def.method, opclasses: def.opclasses,
        },
        drop_name: dropName,
      })
      if (res.applied) {
        toast.success(res.message || `Index ${def.name} applied to ${targetLabel}`)
      } else {
        toast(`Index saved to design — ${targetLabel} not updated: ${res.message || 'unknown error'}`,
          { icon: '⚠️', duration: 6000 })
      }
      setIndexModalOpen(false)
      setEditingIndex(null)
      refreshSync(selectedTable)
    } catch {
      toast.error('Failed to save index')
    } finally {
      setApplyingIndex(false)
    }
  }

  // Delete a designed index: remove from design JSON and drop it from the DB
  // if it exists there.
  const handleIndexDelete = async (ix: TableIndex) => {
    if (!tableInfo || !selectedTable) return
    const dbName = findIndexSync(ix.name)?.db_name
    const msg = dbName
      ? `Delete index "${ix.name}"? It will also be dropped from the ${targetLabel}.`
      : `Delete index "${ix.name}" from the design?`
    if (!window.confirm(msg)) return
    const indexes = (tableInfo.indexes || []).filter((x) => x.name !== ix.name)
    const updated = { ...tableInfo, indexes }
    setTableInfo(updated)
    try {
      await persistTable(updated, selectedTable)
      if (dbName) {
        const res = await applyIndexChange(projectName, selectedTable, {
          target: syncTarget, action: 'drop', drop_name: dbName,
        })
        if (res.applied) {
          toast.success(res.message || `Index ${ix.name} dropped from ${targetLabel}`)
        } else {
          toast(`Removed from design — ${targetLabel} not updated: ${res.message || 'unknown error'}`,
            { icon: '⚠️', duration: 6000 })
        }
      } else {
        toast.success(`Index ${ix.name} removed from design`)
      }
      refreshSync(selectedTable)
    } catch {
      toast.error('Failed to delete index')
    }
  }

  // Drop a DB-only index (exists in the DB but not in the design).
  const handleDropDbOnly = async (dbName: string) => {
    if (!selectedTable) return
    if (!window.confirm(`Drop index "${dbName}" from the ${targetLabel}?`)) return
    try {
      const res = await applyIndexChange(projectName, selectedTable, {
        target: syncTarget, action: 'drop', drop_name: dbName,
      })
      if (res.applied) toast.success(res.message || `Index ${dbName} dropped`)
      else toast.error(res.message || `Failed to drop index ${dbName}`)
      refreshSync(selectedTable)
    } catch {
      toast.error('Failed to drop index')
    }
  }

  // Adopt the live DB's column types into adopted designs + bound
  // transaction models (server-side, with model backups). tbl omitted =
  // every externally-managed table in the project.
  const handleAdoptLiveTypes = useCallback(async (tbl?: string) => {
    setAdoptingTypes(true)
    try {
      const res = await adoptLiveTypes(projectName, syncTarget, tbl)
      if (res.success) {
        const tablesTouched = Object.entries(res.tables || {})
        const colCount = tablesTouched.reduce(
          (n, [, t]) => n + (t.columns_updated?.length || 0), 0)
        const modelCount = Object.keys(res.models_updated || {}).length
        toast.success(
          `Adopted live types: ${colCount} column(s) across ${tablesTouched.length} table(s)`
          + (modelCount ? `, ${modelCount} transaction model(s) updated` : ''),
          { duration: 6000 },
        )
        if (selectedTable) await loadTable(selectedTable)
        if (!tbl) {
          // Bulk run — refresh every table's summary badge.
          tables.forEach((t) => {
            getTableSyncStatus(projectName, t.name, syncTarget)
              .then((r) => { if (r.sync) updateTableSyncSummary(t.name, r.sync) })
              .catch(() => {})
          })
        }
      } else {
        toast.error(res.message || 'Failed to adopt live types')
      }
    } catch {
      toast.error('Failed to adopt live types')
    } finally {
      setAdoptingTypes(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectName, syncTarget, selectedTable, tables, loadTable])

  // Adopt a DB-only index into the design (no DDL needed).
  const handleAdoptDbOnly = async (row: IndexSyncInfo) => {
    if (!tableInfo || !selectedTable) return
    const indexes = [
      ...(tableInfo.indexes || []),
      // Adopt the access method too — a design that recorded a gin index as a
      // b-tree would rebuild it as one on the next deploy.
      {
        name: row.name, columns: row.columns, unique: row.unique,
        ...(row.method && row.method !== 'btree' ? { method: row.method } : {}),
      },
    ]
    const updated = { ...tableInfo, indexes }
    setTableInfo(updated)
    try {
      await persistTable(updated, selectedTable)
      toast.success(`Index ${row.name} added to design`)
      refreshSync(selectedTable)
    } catch {
      toast.error('Failed to add index to design')
    }
  }

  // ---- Export Functions ----
  const downloadFile = (filename: string, content: string, mimeType = 'text/plain') => {
    const blob = new Blob([content], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  }

  const generateTableSQL = (info: TableInfo) => {
    let ddl = `CREATE TABLE ${info.table_name} (\n`
    const colDefs = info.columns.map(col => {
      let def = `  ${col.column_name} ${col.data_type}${col.size ? `(${col.size})` : ''}`
      if (col.primary_key) def += ' PRIMARY KEY'
      if (col.nullable === false) def += ' NOT NULL'
      return def
    })
    ddl += colDefs.join(',\n')
    ddl += '\n);\n'

    info.columns.forEach(col => {
      if (col.foreign_key) {
        const [refTable, refCol] = col.foreign_key.split('.')
        if (refTable && refCol) {
          ddl += `\nALTER TABLE ${info.table_name}\n`
          ddl += `  ADD CONSTRAINT FK_${info.table_name}_${refTable}\n`
          ddl += `  FOREIGN KEY (${col.column_name})\n`
          ddl += `  REFERENCES ${refTable}(${refCol});\n`
        }
      }
    })
    return ddl
  }

  const generateTableXML = (info: TableInfo) => {
    let tableXml = `  <table name="${info.table_name}" description="${info.table_description || ''}">\n`
    tableXml += '    <columns>\n'
    info.columns.forEach(col => {
      tableXml += `      <column name="${col.column_name}" type="${col.data_type}"`
      if (col.primary_key) tableXml += ' key="true"'
      if (col.nullable === false) tableXml += ' mandatory="true"'
      if (col.foreign_key) tableXml += ` foreign_key="${col.foreign_key}"`
      tableXml += ' />\n'
    })
    tableXml += '    </columns>\n'
    tableXml += '  </table>\n'
    return tableXml
  }

  const exportSelectedAsJSON = () => {
    if (!tableInfo) { toast.error('Select a table first'); return }
    const schema = { tables: [tableInfo] }
    downloadFile(`${tableInfo.table_name}.json`, JSON.stringify(schema, null, 2), 'application/json')
    setShowExportMenu(false)
    toast.success(`Exported ${tableInfo.table_name} as JSON`)
  }

  const exportSelectedAsSQL = () => {
    if (!tableInfo) { toast.error('Select a table first'); return }
    let sql = `-- Table: ${tableInfo.table_name}\n-- Generated by Twasta.ai Database Designer\n\n`
    sql += generateTableSQL(tableInfo)
    downloadFile(`${tableInfo.table_name}.sql`, sql)
    setShowExportMenu(false)
    toast.success(`Exported ${tableInfo.table_name} as SQL`)
  }

  const exportSelectedAsXML = () => {
    if (!tableInfo) { toast.error('Select a table first'); return }
    let xmlStr = '<?xml version="1.0" encoding="UTF-8"?>\n<database-schema>\n'
    xmlStr += generateTableXML(tableInfo)
    xmlStr += '</database-schema>\n'
    downloadFile(`${tableInfo.table_name}.xml`, xmlStr, 'application/xml')
    setShowExportMenu(false)
    toast.success(`Exported ${tableInfo.table_name} as XML`)
  }

  const exportAllAs = useCallback(async (format: 'json' | 'sql' | 'xml') => {
    if (tables.length === 0) { toast.error('No tables to export'); return }
    setExportingAll(true)
    setShowExportMenu(false)
    try {
      const allTables = await loadAllTableDetails()

      if (format === 'json') {
        const schema = { tables: allTables }
        downloadFile(`${projectName}_database.json`, JSON.stringify(schema, null, 2), 'application/json')
        toast.success(`Exported all ${allTables.length} tables as JSON`)
      } else if (format === 'sql') {
        let sql = `-- Database Schema for ${projectName}\n-- Generated by Twasta.ai Database Designer\n-- ${allTables.length} tables\n\n`
        allTables.forEach(t => { sql += generateTableSQL(t) + '\n' })
        downloadFile(`${projectName}_database.sql`, sql)
        toast.success(`Exported all ${allTables.length} tables as SQL`)
      } else {
        let xmlStr = '<?xml version="1.0" encoding="UTF-8"?>\n<database-schema>\n'
        allTables.forEach(t => { xmlStr += generateTableXML(t) })
        xmlStr += '</database-schema>\n'
        downloadFile(`${projectName}_database.xml`, xmlStr, 'application/xml')
        toast.success(`Exported all ${allTables.length} tables as XML`)
      }
    } catch {
      toast.error('Failed to load all tables for export')
    } finally {
      setExportingAll(false)
    }
  }, [tables, loadAllTableDetails, projectName])

  const dbColor = resolvedDb ? DB_TYPE_COLOR[resolvedDb.db_type] : 'var(--color-accent)'
  const dbSummary = resolvedDb?.summary || 'Resolving…'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 52px)', overflow: 'hidden', margin: '0 -1.5rem -1.5rem', padding: 0 }}>
      {/* ───────────────────────────────────────────────────────────────
          Prominent database connection bar — always shows which database
          this project deploys to (preview & run), with quick access to
          change it and to sync the project's tables to it.
      ──────────────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0,
        padding: '0.5rem 1rem', borderBottom: '1px solid var(--color-border)',
        background: 'var(--color-bg)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0,
        }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 28, height: 28, borderRadius: 8, flexShrink: 0,
            background: dbColor, color: '#fff',
          }}>
            <Database size={16} />
          </span>
          <span style={{
            fontSize: '0.8125rem', fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.04em', color: 'var(--color-text-secondary)',
          }}>
            Deploys to
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0, flex: 1 }}>
          {resolvedDb && (
            <span style={{
              fontSize: '0.8125rem', fontWeight: 700, padding: '2px 8px',
              borderRadius: 999, background: dbColor, color: '#fff', flexShrink: 0,
            }}>
              {DB_TYPE_LABEL[resolvedDb.db_type]}
            </span>
          )}
          <span
            title={dbSummary}
            style={{
              fontSize: '0.875rem', fontWeight: 500, color: 'var(--color-text)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {dbSummary}
          </span>
          {resolvedDb?.connection_name && (
            <span style={{
              fontSize: '0.8125rem', fontWeight: 500, padding: '1px 8px', flexShrink: 0,
              borderRadius: 999, background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)',
            }}>
              {resolvedDb.connection_name}
            </span>
          )}
          {resolvedDb?.source === 'platform_default' && (
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', flexShrink: 0 }}>
              (default — no custom connection)
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => { setSyncIntro(undefined); setShowSyncTables(true) }}
            title="Sync all project tables to the current database"
          >
            <RefreshCw size={14} />
            Sync tables
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowConnections(true)}
            title="View or change the project's database connection"
          >
            <Link2 size={14} />
            Change database
          </button>
        </div>
      </div>

      <div className="split-layout" style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* Mobile nav backdrop */}
      {mobileNavOpen && <div className="split-nav-backdrop" onClick={() => setMobileNavOpen(false)} />}

      {/* Left panel - table list */}
      <div className={`split-nav ${mobileNavOpen ? '' : 'split-nav-hidden'}`} style={{
        width: 260,
        flexShrink: 0,
        borderRight: '1px solid var(--color-border)',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--color-bg)',
      }}>
        {/* Header */}
        <div style={{
          padding: '0.625rem 0.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            <Database size={14} style={{ color: 'var(--color-accent)' }} />
            <span style={{
              fontSize: '0.8125rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: 'var(--color-text-secondary)',
            }}>Tables</span>
          </div>
          <div style={{ display: 'flex', gap: 2, position: 'relative' }}>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={exportingAll}
              title="Export Schema"
              style={{ padding: '2px 4px' }}
            >
              {exportingAll ? (
                <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <Download size={13} />
              )}
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={handleGenerateDesign}
              disabled={generatingDesign}
              title="Generate database design from transaction models"
              style={{ padding: '2px 4px' }}
            >
              {generatingDesign ? (
                <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <Wand2 size={13} />
              )}
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setShowAddTable(true)}
              title="Add Table"
              style={{ padding: '2px 4px' }}
            >
              <Plus size={13} />
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setShowSync(true)}
              title="External database sync (push/pull tables with an external application DB)"
              style={{ padding: '2px 4px' }}
            >
              <Layers size={13} />
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setShowOffline(true)}
              title="Offline & device replication (run this app on a handheld with no signal)"
              style={{ padding: '2px 4px' }}
            >
              <WifiOff size={13} />
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setShowVectorSearch(true)}
              title="Vector search — let people find master records by describing them ('Bob in the Mumbai sales office'), not by exact spelling"
              style={{ padding: '2px 4px' }}
            >
              <Sparkles size={13} />
            </button>
            {showExportMenu && (
              <div style={{
                position: 'absolute', right: 0, top: '100%', marginTop: 4,
                background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)', boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                minWidth: 200, zIndex: 100, overflow: 'hidden',
              }}>
                {/* All Tables Section */}
                <div style={{
                  padding: '6px 12px', fontSize: '0.8125rem', fontWeight: 600,
                  color: 'var(--color-text-secondary)', textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <Layers size={11} /> All Tables ({tables.length})
                </div>
                {(['json', 'sql', 'xml'] as const).map(fmt => (
                  <button
                    key={`all-${fmt}`}
                    onClick={() => exportAllAs(fmt)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                      padding: '7px 12px', fontSize: '0.8625rem', color: 'var(--color-text)',
                      background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-bg)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    {fmt === 'json' && <FileJson size={14} style={{ color: 'var(--color-info, #0969da)' }} />}
                    {fmt === 'sql' && <FileCode size={14} style={{ color: 'var(--color-success, #1a7f37)' }} />}
                    {fmt === 'xml' && <FileText size={14} style={{ color: 'var(--color-warning, #9a6700)' }} />}
                    Export All as {fmt.toUpperCase()}
                  </button>
                ))}

                {/* Selected Table Section */}
                <div style={{
                  padding: '6px 12px', fontSize: '0.8125rem', fontWeight: 600,
                  color: 'var(--color-text-secondary)', textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  display: 'flex', alignItems: 'center', gap: 6,
                  marginTop: 4,
                }}>
                  <Table2 size={11} /> Selected {selectedTable ? `(${selectedTable})` : ''}
                </div>
                <button
                  onClick={exportSelectedAsJSON}
                  disabled={!tableInfo}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    padding: '7px 12px', fontSize: '0.8625rem',
                    color: tableInfo ? 'var(--color-text)' : 'var(--color-text-secondary)',
                    background: 'transparent', border: 'none',
                    cursor: tableInfo ? 'pointer' : 'default', textAlign: 'left',
                    opacity: tableInfo ? 1 : 0.5,
                  }}
                  onMouseEnter={e => { if (tableInfo) e.currentTarget.style.background = 'var(--color-bg)' }}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <FileJson size={14} style={{ color: 'var(--color-info, #0969da)' }} /> Export as JSON
                </button>
                <button
                  onClick={exportSelectedAsSQL}
                  disabled={!tableInfo}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    padding: '7px 12px', fontSize: '0.8625rem',
                    color: tableInfo ? 'var(--color-text)' : 'var(--color-text-secondary)',
                    background: 'transparent', border: 'none',
                    cursor: tableInfo ? 'pointer' : 'default', textAlign: 'left',
                    opacity: tableInfo ? 1 : 0.5,
                  }}
                  onMouseEnter={e => { if (tableInfo) e.currentTarget.style.background = 'var(--color-bg)' }}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <FileCode size={14} style={{ color: 'var(--color-success, #1a7f37)' }} /> Export as SQL
                </button>
                <button
                  onClick={exportSelectedAsXML}
                  disabled={!tableInfo}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    padding: '7px 12px', fontSize: '0.8625rem',
                    color: tableInfo ? 'var(--color-text)' : 'var(--color-text-secondary)',
                    background: 'transparent', border: 'none',
                    cursor: tableInfo ? 'pointer' : 'default', textAlign: 'left',
                    opacity: tableInfo ? 1 : 0.5,
                  }}
                  onMouseEnter={e => { if (tableInfo) e.currentTarget.style.background = 'var(--color-bg)' }}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <FileText size={14} style={{ color: 'var(--color-warning, #9a6700)' }} /> Export as XML
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Project name badge */}
        <div style={{ padding: '0 0.75rem 0.5rem', flexShrink: 0 }}>
          <div style={{
            padding: '0.375rem 0.625rem',
            background: 'var(--color-bg-secondary)',
            borderRadius: 'var(--radius-md)',
            fontSize: '0.8125rem',
            fontWeight: 500,
            color: 'var(--color-text)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            overflow: 'hidden',
          }}>
            <Database size={13} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
            <span className="truncate">{projectName}</span>
          </div>
        </div>

        {/* ───────────────────────────────────────────────────────────
            SQL Query — pinned at the parent level of the navigation, above
            the table groups, because a query is not scoped to any one table
            and must stay reachable whatever is selected.
        ─────────────────────────────────────────────────────────── */}
        <div style={{ padding: '0 0.5rem 0.5rem', flexShrink: 0 }}>
          <button
            onClick={() => openQueryConsole()}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%',
              padding: '0.5rem 0.625rem', borderRadius: 'var(--radius-md)',
              fontSize: '0.875rem', fontWeight: 600, textAlign: 'left',
              color: railSelection === 'query' ? '#fff' : 'var(--color-text)',
              background: railSelection === 'query'
                ? 'var(--color-accent)' : 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              transition: 'background var(--transition-fast)',
            }}
            title="Write and run a read-only SQL query against the live database"
          >
            <Terminal size={14} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1 }}>SQL Query</span>
          </button>
        </div>

        {/* Table list */}
        <div style={{ flex: 1, overflow: 'auto', padding: '0.25rem 0.5rem' }}>
          {loadingTables ? (
            <div className="flex items-center justify-center p-4">
              <Loader2 size={18} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
            </div>
          ) : (
            <>
              {/* ── Project tables (have a Database_Design entry) ── */}
              <button
                onClick={() => setProjectGroupOpen((v) => !v)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4, width: '100%',
                  padding: '0.25rem 0.375rem', fontSize: '0.8125rem', fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                  color: 'var(--color-text-secondary)', background: 'transparent',
                  border: 'none', cursor: 'pointer', textAlign: 'left',
                }}
              >
                {projectGroupOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                <span>Project</span>
                <span style={{ opacity: 0.6 }}>({tables.length})</span>
              </button>
              {projectGroupOpen && tables.map((t) => {
                const syncState = tableSyncSummary[t.name]
                const isSelected = railSelection === 'table' && selectedTable === t.name && !selectedLiveTable
                const expanded = expandedRailTable === t.name
                const cols = catalog.columnsFor(t.name)
                return (
                  <div key={t.name}>
                    <div
                      style={{
                        display: 'flex', alignItems: 'center', gap: 2,
                        borderRadius: 'var(--radius-md)', marginBottom: 2,
                        background: isSelected ? 'var(--color-bg-secondary)' : 'transparent',
                      }}
                      onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = 'var(--color-bg-secondary)' }}
                      onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent' }}
                    >
                      <button
                        onClick={() => toggleRailTable(t.name)}
                        title={expanded ? 'Hide columns' : 'Show columns (drag them into a query)'}
                        style={{
                          display: 'flex', alignItems: 'center', padding: '0 1px',
                          background: 'transparent', border: 'none', cursor: 'pointer',
                          color: 'var(--color-text-secondary)', flexShrink: 0,
                        }}
                      >
                        {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      </button>
                      <button
                        draggable
                        onDragStart={(e) => dragText(e, t.name)}
                        onClick={() => { loadTable(t.name); setMobileNavOpen(false) }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.375rem',
                          flex: 1, minWidth: 0, padding: '0.375rem 0.5rem 0.375rem 0',
                          fontSize: '0.875rem', background: 'transparent', border: 'none',
                          color: isSelected ? 'var(--color-text)' : 'var(--color-text-secondary)',
                          textAlign: 'left', cursor: 'pointer',
                        }}
                      >
                        <Table2 size={14} style={{ flexShrink: 0 }} />
                        <span className="truncate" style={{ flex: 1 }}>{t.name}</span>
                        {syncState === 'synced' && (
                          <span title="Synced with DB"><CheckCircle2 size={12} style={{ color: '#22c55e', flexShrink: 0 }} /></span>
                        )}
                        {syncState === 'mismatch' && (
                          <span title="Mismatch with DB"><AlertTriangle size={12} style={{ color: '#f59e0b', flexShrink: 0 }} /></span>
                        )}
                        {syncState === 'external_drift' && (
                          <span title="Adopted table — design types differ from the live DB (use Adopt live types)"><RefreshCw size={12} style={{ color: '#3b82f6', flexShrink: 0 }} /></span>
                        )}
                        {syncState === 'not_in_db' && (
                          <span title="Table not in DB"><XCircle size={12} style={{ color: '#ef4444', flexShrink: 0 }} /></span>
                        )}
                      </button>
                    </div>
                    {expanded && (
                      <RailColumns
                        columns={cols}
                        table={t.name}
                        onDragText={dragText}
                        onInsert={(text) => consoleInsertRef.current?.(text)}
                      />
                    )}
                  </div>
                )
              })}
              {projectGroupOpen && tables.length === 0 && (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  gap: '0.5rem', padding: '1.5rem 1rem', color: 'var(--color-text-secondary)',
                }}>
                  <Table2 size={24} style={{ opacity: 0.3 }} />
                  <p style={{ fontSize: '0.875rem', margin: 0 }}>No tables yet</p>
                </div>
              )}

              {/* ── In database only (physical tables with no design) ── */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 4, marginTop: '0.75rem',
                fontSize: '0.8125rem', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.05em',
                color: 'var(--color-text-secondary)',
              }}>
                <button
                  onClick={() => setDbOnlyGroupOpen((v) => !v)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4, flex: 1, minWidth: 0,
                    padding: '0.25rem 0.375rem', font: 'inherit', letterSpacing: 'inherit',
                    textTransform: 'inherit', color: 'inherit', background: 'transparent',
                    border: 'none', cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  {dbOnlyGroupOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                  <span className="truncate">In database only</span>
                  <span style={{ opacity: 0.6 }}>({dbOnlyTables.length})</span>
                </button>
                <button
                  onClick={() => void catalog.refresh()}
                  disabled={catalog.loading}
                  title="Refresh the live table list"
                  style={{
                    display: 'flex', alignItems: 'center', padding: '0 0.375rem 0 0',
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    color: 'var(--color-text-secondary)', flexShrink: 0,
                  }}
                >
                  <RefreshCw
                    size={11}
                    style={catalog.loading ? { animation: 'spin 1s linear infinite' } : undefined}
                  />
                </button>
              </div>

              {!dbOnlyGroupOpen ? null : catalog.error ? (
                <div style={{
                  padding: '0.375rem 0.625rem', fontSize: '0.8125rem', lineHeight: 1.4,
                  color: 'var(--color-text-secondary)',
                }}>
                  <span style={{ color: '#f59e0b' }}>Live database unavailable.</span>{' '}
                  <span title={catalog.error}>{catalog.error.slice(0, 90)}</span>
                  <button
                    onClick={() => void catalog.refresh()}
                    style={{
                      display: 'block', marginTop: 4, padding: 0, background: 'transparent',
                      border: 'none', cursor: 'pointer', color: 'var(--color-accent)',
                      fontSize: '0.8125rem',
                    }}
                  >
                    Retry
                  </button>
                </div>
              ) : catalog.loading && dbOnlyTables.length === 0 ? (
                <div style={{ padding: '0.5rem 0.625rem' }}>
                  <Loader2 size={13} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-text-secondary)' }} />
                </div>
              ) : dbOnlyTables.length === 0 ? (
                <div style={{
                  padding: '0.375rem 0.625rem', fontSize: '0.8125rem',
                  color: 'var(--color-text-secondary)',
                }}>
                  Every live table is in the project.
                </div>
              ) : dbOnlyTables.map((t) => {
                const isSelected = railSelection === 'table' && selectedLiveTable === t.name
                const expanded = expandedRailTable === t.name
                const cols = catalog.columnsFor(t.name)
                return (
                  <div key={`live-${t.name}`}>
                    <div
                      style={{
                        display: 'flex', alignItems: 'center', gap: 2,
                        borderRadius: 'var(--radius-md)', marginBottom: 2,
                        background: isSelected ? 'var(--color-bg-secondary)' : 'transparent',
                      }}
                      onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = 'var(--color-bg-secondary)' }}
                      onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent' }}
                    >
                      <button
                        onClick={() => toggleRailTable(t.name)}
                        title={expanded ? 'Hide columns' : 'Show columns (drag them into a query)'}
                        style={{
                          display: 'flex', alignItems: 'center', padding: '0 1px',
                          background: 'transparent', border: 'none', cursor: 'pointer',
                          color: 'var(--color-text-secondary)', flexShrink: 0,
                        }}
                      >
                        {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      </button>
                      <button
                        draggable
                        onDragStart={(e) => dragText(e, t.name)}
                        onClick={() => selectLiveTable(t.name)}
                        title={`${t.name} — in the database but not in the project design`}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.375rem',
                          flex: 1, minWidth: 0, padding: '0.375rem 0.5rem 0.375rem 0',
                          fontSize: '0.875rem', background: 'transparent', border: 'none',
                          color: isSelected ? 'var(--color-text)' : '#c08a2e',
                          textAlign: 'left', cursor: 'pointer',
                        }}
                      >
                        <DatabaseZap size={14} style={{ flexShrink: 0, opacity: 0.8 }} />
                        <span className="truncate" style={{ flex: 1 }}>{t.name}</span>
                        {t.rows !== null && t.rows !== undefined && (
                          <span
                            title={t.rows_estimated ? 'Approximate row count' : 'Row count'}
                            style={{ fontSize: '0.8125rem', opacity: 0.7, flexShrink: 0 }}
                          >
                            {t.rows_estimated ? '~' : ''}{compactCount(t.rows)}
                          </span>
                        )}
                      </button>
                    </div>
                    {expanded && (
                      <RailColumns
                        columns={cols}
                        table={t.name}
                        onDragText={dragText}
                        onInsert={(text) => consoleInsertRef.current?.(text)}
                      />
                    )}
                  </div>
                )
              })}

              {/* ── Designed but never deployed ── */}
              {catalog.designOnly.length > 0 && (
                <div style={{
                  marginTop: '0.75rem', padding: '0.375rem 0.625rem',
                  fontSize: '0.8125rem', lineHeight: 1.5,
                  color: 'var(--color-text-secondary)',
                  borderTop: '1px solid var(--color-border)',
                }}>
                  <span style={{ fontWeight: 600 }}>
                    {catalog.designOnly.length} designed, not deployed
                  </span>
                  <div title={catalog.designOnly.join(', ')} className="truncate">
                    {catalog.designOnly.join(', ')}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Add table */}
        <div style={{ padding: '0.75rem', flexShrink: 0 }}>
          {showAddTable ? (
            <div className="flex flex-col gap-2">
              <input
                type="text"
                value={newTableName}
                onChange={(e) => setNewTableName(e.target.value)}
                placeholder="Table name..."
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddTable(); if (e.key === 'Escape') setShowAddTable(false) }}
                autoFocus
                style={{ fontSize: '0.875rem' }}
              />
              <div className="flex gap-2">
                <button className="btn btn-primary btn-sm flex-1" onClick={handleAddTable}>Add</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setShowAddTable(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <button
              className="btn btn-secondary btn-sm w-full"
              onClick={() => setShowAddTable(true)}
            >
              <Plus size={14} />
              Add Table
            </button>
          )}
        </div>
      </div>

      {/* Main area - table editor */}
      <div className="split-content" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Mobile floating nav toggle */}
        <button
          className="split-nav-toggle"
          onClick={() => setMobileNavOpen(true)}
        >
          <span className="material-icons" style={{ fontSize: 17 }}>table_chart</span>
          Tables
        </button>

        {/* ─────────────────────────────────────────────────────────────
            SQL Query is a parent-level destination: it replaces the whole
            table pane rather than living inside one table's tabs.
        ───────────────────────────────────────────────────────────── */}
        {railSelection === 'query' ? (
          <SqlConsoleView
            project={projectName}
            target={syncTarget}
            catalog={catalog}
            initialSql={querySeed}
            insertRef={consoleInsertRef}
          />
        ) : selectedLiveTable ? (
          /* Same Design | Data interface as a project table. Design renders
             the live structure read-only — there is no design file to edit
             until the table is adopted. */
          <>
            <TableTabs tabs={['design', 'data']} active={tableTab} onChange={setTableTab} />
            <div style={{ flex: 1, minHeight: 0 }}>
              {tableTab === 'design' ? (
                <LiveStructureView
                  project={projectName}
                  target={syncTarget}
                  table={selectedLiveTable}
                  canEdit={canEdit}
                  onAdopted={handleAdoptedTable}
                />
              ) : (
                <DataBrowserView
                  project={projectName}
                  target={syncTarget}
                  table={selectedLiveTable}
                  inProject={false}
                  canEdit={canEdit}
                  onOpenInQuery={openQueryConsole}
                  onAdopted={() => handleAdoptedTable(selectedLiveTable.toUpperCase())}
                />
              )}
            </div>
          </>
        ) : selectedTable && tableTab === 'data' ? (
          <>
            <TableTabs tabs={['design', 'data']} active="data" onChange={setTableTab} />
            <div style={{ flex: 1, minHeight: 0 }}>
              <DataBrowserView
                project={projectName}
                target={syncTarget}
                table={selectedTable}
                inProject
                canEdit={canEdit}
                onOpenInQuery={openQueryConsole}
                onAdopted={() => { /* already in project */ }}
              />
            </div>
          </>
        ) : !selectedTable ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-3" style={{ color: 'var(--color-text-secondary)' }}>
              <Database size={48} style={{ opacity: 0.3 }} />
              <p>Select a table from the list to edit</p>
            </div>
          </div>
        ) : loadingTable ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
          </div>
        ) : tableInfo ? (
          <>
            <TableTabs tabs={['design', 'data']} active="design" onChange={setTableTab} />
            {/* Table header */}
            <div style={{
              padding: '1rem 1.25rem',
              borderBottom: '1px solid var(--color-border)',
              flexShrink: 0,
            }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Table2 size={20} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <input
                      type="text"
                      value={tableInfo.table_name}
                      onChange={(e) => setTableInfo({ ...tableInfo, table_name: e.target.value })}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        fontSize: '1.1875rem',
                        fontWeight: 600,
                        lineHeight: 1.4,
                        color: 'var(--color-text)',
                        padding: '2px 0',
                        width: '100%',
                      }}
                    />
                    <input
                      type="text"
                      value={tableInfo.table_description}
                      onChange={(e) => setTableInfo({ ...tableInfo, table_description: e.target.value })}
                      placeholder="Table description..."
                      style={{
                        background: 'transparent',
                        border: 'none',
                        fontSize: '0.875rem',
                        color: 'var(--color-text-secondary)',
                        padding: 0,
                        width: '100%',
                        marginTop: 2,
                      }}
                    />
                    {/* Adopted (externally managed) toggle: managed:false makes
                        schema-sync/deploy bind-only for this table and stops
                        the generators inventing columns (audit incl.) on it. */}
                    <label
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
                        marginTop: 4, cursor: canEdit ? 'pointer' : 'default',
                        userSelect: 'none',
                      }}
                      title="An adopted table belongs to an external application: twasta binds to it exactly as it exists — deploy never creates, alters or indexes it, and generated models never add audit or other invented columns to it."
                    >
                      <input
                        type="checkbox"
                        checked={tableInfo.managed === false}
                        disabled={!canEdit}
                        onChange={(e) => {
                          const adopted = e.target.checked
                          setTableInfo({
                            ...tableInfo,
                            managed: adopted ? false : undefined,
                            source: adopted
                              ? 'external'
                              : (tableInfo.source === 'external' ? undefined : tableInfo.source),
                          })
                        }}
                      />
                      Adopted (externally managed) — bind-only: twasta never
                      creates or alters this table, and never adds audit columns
                      to it
                      {tableInfo.managed === false && (
                        <span
                          style={{
                            fontSize: '0.8125rem', fontWeight: 600,
                            padding: '1px 8px', borderRadius: 999,
                            background: 'var(--color-bg-secondary)',
                            border: '1px solid var(--color-border)',
                          }}
                        >
                          Adopted
                        </span>
                      )}
                    </label>
                  </div>
                </div>
                <div className="flex items-center gap-2" style={{ position: 'relative' }}>
                  {canEdit ? (
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? (
                        <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                      ) : (
                        <Save size={14} />
                      )}
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  ) : (
                    <ReadOnlyBadge />
                  )}
                  <div ref={syncMenuRef} style={{ position: 'relative' }}>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setSyncMenuOpen((v) => !v)}
                      aria-haspopup="menu"
                      aria-expanded={syncMenuOpen}
                      title="Verification target"
                      style={{ padding: '0.375rem' }}
                    >
                      <MoreVertical size={16} />
                    </button>
                    {syncMenuOpen && (
                      <div
                        role="menu"
                        style={{
                          position: 'absolute',
                          top: 'calc(100% + 4px)',
                          right: 0,
                          minWidth: 260,
                          background: 'var(--color-bg)',
                          border: '1px solid var(--color-border)',
                          borderRadius: 6,
                          boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                          zIndex: 30,
                          padding: 4,
                        }}
                      >
                        <div style={{
                          padding: '6px 10px',
                          fontSize: 13,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          color: 'var(--color-text-secondary)',
                        }}>
                          Verify against
                        </div>
                        {(
                          [
                            { key: 'preview', label: 'Live preview database', hint: 'Default — sandbox DB used by Live Preview' },
                            { key: 'deployment', label: 'Deployment database', hint: 'Active deployment profile target' },
                          ] as Array<{ key: SyncTarget; label: string; hint: string }>
                        ).map((opt) => {
                          const active = syncTarget === opt.key
                          return (
                            <button
                              key={opt.key}
                              role="menuitemradio"
                              aria-checked={active}
                              onClick={() => { setSyncTarget(opt.key); setSyncMenuOpen(false) }}
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 8,
                                width: '100%',
                                textAlign: 'left',
                                padding: '8px 10px',
                                background: active ? 'var(--color-bg-secondary)' : 'transparent',
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                                color: 'var(--color-text)',
                              }}
                            >
                              <span style={{ width: 14, display: 'inline-flex', justifyContent: 'center', marginTop: 2 }}>
                                {active && <Check size={14} style={{ color: 'var(--color-accent)' }} />}
                              </span>
                              <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{opt.label}</span>
                                <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>{opt.hint}</span>
                              </span>
                            </button>
                          )
                        })}
                        {canEdit && (
                          <>
                            <div style={{ borderTop: '1px solid var(--color-border)', margin: '4px 0' }} />
                            <button
                              role="menuitem"
                              disabled={adoptingTypes}
                              onClick={() => { setSyncMenuOpen(false); void handleAdoptLiveTypes() }}
                              style={{
                                display: 'flex', alignItems: 'flex-start', gap: 8,
                                width: '100%', textAlign: 'left', padding: '8px 10px',
                                background: 'transparent', border: 'none',
                                borderRadius: 4, cursor: 'pointer', color: 'var(--color-text)',
                              }}
                            >
                              <span style={{ width: 14, display: 'inline-flex', justifyContent: 'center', marginTop: 2 }}>
                                <RefreshCw size={13} style={{ color: '#3b82f6' }} />
                              </span>
                              <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Re-sync adopted tables from live DB</span>
                                <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                                  Adopt the live database's column types into every externally-managed
                                  table design and its bound transaction models
                                </span>
                              </span>
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* DB Sync Status Bar */}
              {syncLoading && (
                <div style={{ marginTop: 8, fontSize: '0.8125rem', color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                  Checking DB sync status...
                </div>
              )}
              {syncStatus && !syncLoading && (
                <div style={{
                  marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', fontSize: '0.8125rem',
                }}>
                  <span
                    title={`Comparing design against the ${syncTarget === 'preview' ? 'live preview' : 'deployment'} database`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 8px', borderRadius: 10, fontSize: 13, fontWeight: 500,
                      color: 'var(--color-text-secondary)', background: 'var(--color-bg-secondary)',
                      border: '1px solid var(--color-border)',
                    }}
                  >
                    <Database size={11} />
                    {syncTarget === 'preview' ? 'vs Live Preview' : 'vs Deployment'}
                  </span>
                  {!syncStatus.table_exists ? (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 8px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                      color: '#fff', background: '#ef4444',
                    }}>
                      <XCircle size={11} /> Table not in DB
                    </span>
                  ) : syncStatus.managed === false ? (
                    <>
                      {/* Adopted table: live DB is the source of truth */}
                      {syncStatus.columns.every((c) => c.status === 'synced') ? (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '2px 8px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                          color: '#fff', background: '#22c55e',
                        }}>
                          <CheckCircle2 size={11} /> Adopted — synced with live DB
                        </span>
                      ) : (
                        <>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '2px 8px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                            color: '#fff', background: '#3b82f6',
                          }}>
                            <RefreshCw size={11} /> Adopted — live DB is source of truth
                          </span>
                          <span style={{ color: '#3b82f6', fontSize: 13 }}>
                            {syncStatus.columns.filter((c) => c.status !== 'synced').length} column(s) differ from the live DB
                          </span>
                          <button
                            className="btn btn-secondary btn-sm"
                            disabled={adoptingTypes || !canEdit}
                            onClick={() => { if (selectedTable) void handleAdoptLiveTypes(selectedTable) }}
                            title="Rewrite this design's column types from the live database and update every bound transaction model (backups are kept under .twasta/model-backups/)"
                            style={{ fontSize: 13, padding: '2px 8px' }}
                          >
                            {adoptingTypes
                              ? <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
                              : <RefreshCw size={11} />} Adopt live types
                          </button>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {syncStatus.columns.every((c) => c.status === 'synced') && syncStatus.pk_exists_in_db ? (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '2px 8px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                          color: '#fff', background: '#22c55e',
                        }}>
                          <CheckCircle2 size={11} /> Synced with DB
                        </span>
                      ) : (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '2px 8px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                          color: '#fff', background: '#f59e0b',
                        }}>
                          <AlertTriangle size={11} /> Mismatch with DB
                        </span>
                      )}
                      {syncStatus.pk_exists_in_db ? (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '2px 8px', borderRadius: 10, fontSize: 13, fontWeight: 500,
                          color: '#a855f7', background: 'rgba(168,85,247,0.1)',
                        }}>
                          <Key size={11} /> PK: {syncStatus.primary_key_in_db.join(', ')}
                        </span>
                      ) : syncStatus.primary_key_in_design.length > 0 ? (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '2px 8px', borderRadius: 10, fontSize: 13, fontWeight: 500,
                          color: '#ef4444', background: 'rgba(239,68,68,0.1)',
                        }}>
                          <Key size={11} /> No PK constraint in DB
                        </span>
                      ) : null}
                      {syncStatus.columns.filter((c) => c.status === 'missing').length > 0 && (
                        <span style={{ color: '#f59e0b', fontSize: 13 }}>
                          {syncStatus.columns.filter((c) => c.status === 'missing').length} column(s) not in DB
                        </span>
                      )}
                      {syncStatus.columns.filter((c) => c.status === 'type_mismatch').length > 0 && (
                        <span style={{ color: '#f59e0b', fontSize: 13 }}>
                          {syncStatus.columns.filter((c) => c.status === 'type_mismatch').length} type mismatch(es)
                        </span>
                      )}
                      {syncStatus.columns.filter((c) => c.status === 'extra_in_db').length > 0 && (
                        <span style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>
                          {syncStatus.columns.filter((c) => c.status === 'extra_in_db').length} extra in DB
                        </span>
                      )}
                      {(syncStatus.indexes || []).filter((i) => i.status === 'missing').length > 0 && (
                        <span style={{ color: '#f59e0b', fontSize: 13 }}>
                          {(syncStatus.indexes || []).filter((i) => i.status === 'missing').length} index(es) not in DB
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Column list */}
            <div style={{ flex: 1, overflow: 'auto', padding: '0.75rem 1rem' }}>
              <div className="table-container" style={{ fontSize: '0.875rem', overflowX: 'auto' }}>
                <table style={{ minWidth: 580, width: '100%' }}>
                  <colgroup>
                    <col style={{ width: 30 }} />   {/* # */}
                    <col style={{ width: 24 }} />   {/* Drag */}
                    <col style={{ minWidth: 140 }} /> {/* Column Name — min 140px, grows with space */}
                    <col style={{ width: 110 }} />   {/* Data Type */}
                    <col style={{ width: 65 }} />    {/* Size */}
                    <col style={{ width: 40 }} />    {/* Null */}
                    <col style={{ width: 32 }} />    {/* PK */}
                    <col style={{ width: 70 }} />    {/* Icons (FK, Desc, Status) */}
                    <col style={{ width: 32 }} />    {/* Del */}
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={{ padding: '0.375rem 0.125rem', textAlign: 'center', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>#</th>
                      <th style={{ padding: '0.375rem 0' }}></th>
                      <th style={{ padding: '0.375rem 0.375rem' }}>Column Name</th>
                      <th style={{ padding: '0.375rem 0.25rem' }}>Data Type</th>
                      <th style={{ padding: '0.375rem 0.25rem' }}>Size</th>
                      <th style={{ padding: '0.375rem 0.125rem', textAlign: 'center' }}>Null</th>
                      <th style={{ padding: '0.375rem 0.125rem', textAlign: 'center' }}>PK</th>
                      <th style={{ padding: '0.375rem 0.125rem', textAlign: 'center' }}></th>
                      <th style={{ padding: '0.375rem 0.125rem' }}></th>
                    </tr>
                  </thead>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                      <tbody>
                        {tableInfo.columns.map((col, idx) => (
                          <SortableRow
                            key={sortableIds[idx]}
                            id={sortableIds[idx]}
                            idx={idx}
                            col={col}
                            syncStatus={syncStatus}
                            dataTypes={dataTypes}
                            updateColumn={updateColumn}
                            removeColumn={removeColumn}
                            fkEditIdx={fkEditIdx}
                            setFkEditIdx={setFkEditIdx}
                            descEditIdx={descEditIdx}
                            setDescEditIdx={setDescEditIdx}
                            onAdoptLiveTypes={() => { if (selectedTable) void handleAdoptLiveTypes(selectedTable) }}
                          />
                        ))}
                        {/* Extra columns that exist in DB but not in design */}
                        {syncStatus && syncStatus.columns
                          .filter((c) => c.status === 'extra_in_db')
                          .map((c) => (
                            <tr key={`extra-${c.column_name}`} style={{ opacity: 0.6 }}>
                              <td style={{ padding: '0.25rem 0.125rem' }}></td>
                              <td style={{ padding: '0.25rem 0' }}></td>
                              <td style={{ padding: '0.25rem 0.375rem', fontSize: '0.875rem', fontStyle: 'italic', color: 'var(--color-text-secondary)' }}>
                                {c.column_name}
                              </td>
                              <td style={{ padding: '0.25rem 0.25rem', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                                {c.db_type}
                              </td>
                              <td style={{ padding: '0.25rem' }}></td>
                              <td style={{ padding: '0.25rem' }}></td>
                              <td style={{ padding: '0.25rem', textAlign: 'center' }}>
                                {c.is_pk_in_db && <Key size={11} style={{ color: '#a855f7' }} />}
                              </td>
                              <td style={{ padding: '0.25rem 0.125rem', textAlign: 'center' }}>
                                <span title="Only in DB"><AlertTriangle size={13} style={{ color: '#f59e0b' }} /></span>
                              </td>
                              <td style={{ padding: '0.25rem' }}></td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </SortableContext>
                  </DndContext>
                </table>
              </div>

              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                flexWrap: 'wrap', marginTop: '0.75rem',
              }}>
                <button className="btn btn-secondary btn-sm" onClick={addColumn}>
                  <Plus size={14} />
                  Add Column
                </button>
                {/* Columns the table has but the design does not. Syncing
                    deploys the design ONTO the database, so it never brings
                    these back the other way — this does. */}
                {extraDbColumnCount > 0 && canEdit && (
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={handleAdoptDbColumns}
                    disabled={adoptingDbColumns}
                    title={`Add the ${extraDbColumnCount} column(s) that exist in the ${syncTarget === 'preview' ? 'live preview' : 'deployment'} database but not in this design`}
                  >
                    {adoptingDbColumns
                      ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                      : <Download size={14} />}
                    Add {extraDbColumnCount} column{extraDbColumnCount === 1 ? '' : 's'} from DB
                  </button>
                )}
              </div>

              {/* Indexes — fully editable. Designed indexes (manual + composite
                  indexes from enforced filter sets) live in the design JSON;
                  add/edit/delete persists the design and immediately applies
                  the DDL to the selected verification target DB. DB-only
                  indexes can be adopted into the design or dropped. */}
              {(() => {
                type IdxRow = TableIndex & {
                  status: IndexSyncInfo['status'] | 'design'
                  db_name: string | null
                  db_method?: string | null
                }
                const syncIdx = syncStatus?.indexes || []
                const syncByName = new Map(syncIdx.map((s) => [s.name.toLowerCase(), s]))
                const designRows: IdxRow[] = (tableInfo.indexes || []).map((ix) => {
                  const s = syncByName.get(ix.name.toLowerCase())
                  return {
                    ...ix,
                    status: s ? s.status : ('design' as const),
                    db_name: s?.db_name ?? null,
                    db_method: s?.db_method ?? null,
                  }
                })
                const extraRows = syncIdx.filter((s) => s.status === 'extra_in_db')
                const total = designRows.length + extraRows.length
                const checked = !!syncStatus
                const badge = (status: IdxRow['status']) => {
                  const base = {
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '1px 7px', borderRadius: 10, fontSize: 13,
                    fontWeight: 600, whiteSpace: 'nowrap' as const,
                  }
                  if (status === 'synced') {
                    return (
                      <span title="This index exists in the database" style={{ ...base, color: '#fff', background: '#22c55e' }}>
                        <CheckCircle2 size={11} /> In DB
                      </span>
                    )
                  }
                  if (status === 'missing') {
                    return (
                      <span title="Designed but not found in the database" style={{ ...base, color: '#fff', background: '#f59e0b' }}>
                        <AlertTriangle size={11} /> Not in DB
                      </span>
                    )
                  }
                  if (status === 'method_differs') {
                    return (
                      <span title="An index on these columns exists in the database, but was built with a different access method — it answers different queries than the design asks for. Edit and re-apply to rebuild it." style={{ ...base, color: '#fff', background: '#f59e0b' }}>
                        <AlertTriangle size={11} /> Wrong method
                      </span>
                    )
                  }
                  if (status === 'extra_in_db') {
                    return (
                      <span title="Exists in the database but not in the design" style={{ ...base, color: 'var(--color-text-secondary)', background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)' }}>
                        <Database size={11} /> DB only
                      </span>
                    )
                  }
                  return null
                }
                const iconBtn: React.CSSProperties = {
                  background: 'none', border: 'none', cursor: 'pointer', padding: 3,
                  display: 'flex', alignItems: 'center',
                  color: 'var(--color-text-secondary)',
                }
                return (
                  <div style={{ marginTop: '1.5rem' }}>
                    {/* The label can be long ("… — checked against live preview
                        DB"). Without a shrinkable label the Add button gets
                        pushed past the right edge of the scrolling pane, where
                        it reads as "there is no way to add an index". */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem',
                      marginBottom: '0.5rem', flexWrap: 'wrap',
                    }}>
                      <div style={{
                        fontSize: '0.875rem', fontWeight: 600,
                        color: 'var(--color-text-secondary)',
                        flex: '1 1 auto', minWidth: 0,
                      }}>
                        Indexes ({total})
                        <span style={{ fontWeight: 400 }}>
                          {checked
                            ? ` — checked against ${syncTarget === 'preview' ? 'live preview' : 'deployment'} DB`
                            : ' — DB not checked yet'}
                        </span>
                      </div>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => { setEditingIndex(null); setIndexModalOpen(true) }}
                        style={{ marginLeft: 'auto', flexShrink: 0 }}
                        title={`Add an index (applied to the ${targetLabel})`}
                      >
                        <ListPlus size={13} />
                        Add Index
                      </button>
                    </div>
                    {total === 0 ? (
                      <div style={{
                        border: '1px dashed var(--color-border)',
                        borderRadius: 'var(--radius-md)',
                        padding: '0.75rem', fontSize: '0.875rem',
                        color: 'var(--color-text-secondary)', fontStyle: 'italic',
                      }}>
                        No indexes defined. Add one to speed up lookups and filtering on large tables.
                      </div>
                    ) : (
                      <div style={{
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-md)', overflow: 'hidden',
                      }}>
                        {designRows.map((ix, i) => (
                          <div key={`design-${ix.name}-${i}`} style={{
                            display: 'flex', alignItems: 'center', gap: '0.75rem',
                            padding: '0.4rem 0.75rem', fontSize: '0.875rem',
                            borderBottom: (i < designRows.length - 1 || extraRows.length > 0) ? '1px solid var(--color-border)' : 'none',
                          }}>
                            <code style={{ color: 'var(--color-text)' }}>{ix.name}</code>
                            <span style={{ color: 'var(--color-text-secondary)' }}>
                              ({ix.columns.join(', ')}){ix.unique ? ' · unique' : ''}
                              {ix.method && ix.method !== 'btree' ? ` · ${ix.method}` : ''}
                              {ix.status === 'method_differs' && ix.db_method
                                ? ` · DB has ${ix.db_method}`
                                : ''}
                            </span>
                            <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              {badge(ix.status)}
                              <button
                                onClick={() => { setEditingIndex({ name: ix.name, columns: ix.columns, unique: ix.unique, method: ix.method, opclasses: ix.opclasses }); setIndexModalOpen(true) }}
                                title="Edit index"
                                style={iconBtn}
                              >
                                <Pencil size={13} />
                              </button>
                              <button
                                onClick={() => handleIndexDelete({ name: ix.name, columns: ix.columns, unique: ix.unique, method: ix.method })}
                                title="Delete index (design + DB)"
                                style={iconBtn}
                              >
                                <Trash2 size={13} style={{ color: 'var(--color-error)' }} />
                              </button>
                            </span>
                          </div>
                        ))}
                        {extraRows.map((ix, i) => (
                          <div key={`extra-${ix.name}-${i}`} style={{
                            display: 'flex', alignItems: 'center', gap: '0.75rem',
                            padding: '0.4rem 0.75rem', fontSize: '0.875rem',
                            borderBottom: i < extraRows.length - 1 ? '1px solid var(--color-border)' : 'none',
                            opacity: 0.75,
                          }}>
                            <code style={{ color: 'var(--color-text)', fontStyle: 'italic' }}>{ix.name}</code>
                            <span style={{ color: 'var(--color-text-secondary)' }}>
                              ({ix.columns.join(', ')}){ix.unique ? ' · unique' : ''}
                              {ix.method && ix.method !== 'btree' ? ` · ${ix.method}` : ''}
                            </span>
                            <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              {badge('extra_in_db')}
                              <button
                                onClick={() => handleAdoptDbOnly(ix)}
                                title="Add this DB index to the design"
                                style={iconBtn}
                              >
                                <Plus size={13} />
                              </button>
                              <button
                                onClick={() => handleDropDbOnly(ix.db_name || ix.name)}
                                title={`Drop from the ${targetLabel}`}
                                style={iconBtn}
                              >
                                <Trash2 size={13} style={{ color: 'var(--color-error)' }} />
                              </button>
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })()}

              {/* Index add/edit modal */}
              <IndexEditorModal
                open={indexModalOpen}
                onClose={() => { setIndexModalOpen(false); setEditingIndex(null) }}
                tableName={tableInfo.table_name}
                availableColumns={tableInfo.columns.map((c) => c.column_name).filter(Boolean)}
                columnTypes={Object.fromEntries(tableInfo.columns.map((c) => [
                  c.column_name,
                  (c.native_type || '').trim()
                    || `${c.data_type || ''}${c.size ? `(${c.size})` : ''}`,
                ]))}
                methods={indexMethods}
                otherIndexNames={(tableInfo.indexes || [])
                  .filter((ix) => !editingIndex || ix.name !== editingIndex.name)
                  .map((ix) => ix.name.toLowerCase())}
                initial={editingIndex}
                onSubmit={handleIndexSubmit}
                saving={applyingIndex}
                targetLabel={targetLabel}
              />

              {/* Table Usage */}
              {tableUsage && tableUsage.objects && (tableUsage.objects as unknown[]).length > 0 && (
                <div style={{ marginTop: '1.5rem' }}>
                  <button
                    onClick={() => setUsageExpanded(!usageExpanded)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      background: 'none',
                      color: 'var(--color-text-secondary)',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                      padding: '0.5rem 0',
                    }}
                  >
                    {usageExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    Table Usage ({(tableUsage.objects as unknown[]).length} references)
                  </button>
                  {usageExpanded && (
                    <div style={{
                      marginTop: '0.5rem',
                      padding: '0.75rem',
                      background: 'var(--color-bg-secondary)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--color-border)',
                      fontSize: '0.875rem',
                    }}>
                      <pre style={{
                        whiteSpace: 'pre-wrap',
                        color: 'var(--color-text-secondary)',
                        fontFamily: 'monospace',
                        fontSize: '0.8125rem',
                        margin: 0,
                      }}>
                        {JSON.stringify(tableUsage.objects, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>

      </div>

      {/* Per-project database connections (preview/run DB + sync targets) */}
      <ConnectionsPanel
        project={projectName}
        open={showConnections}
        onClose={() => setShowConnections(false)}
        onPreviewDbChanged={handlePreviewDbChanged}
        onDialectMigration={handleDialectMigration}
      />

      {/* Offline & device replication */}
      <OfflinePanel
        project={projectName}
        open={showOffline}
        onClose={() => setShowOffline(false)}
      />

      {/* Semantic search over this app's master records */}
      <VectorSearchPanel
        project={projectName}
        open={showVectorSearch}
        onClose={() => setShowVectorSearch(false)}
      />

      {/* External-database sync mappings + status */}
      <SyncPanel
        project={projectName}
        open={showSync}
        onClose={() => setShowSync(false)}
      />

      {/* Sync project tables to the current database, with live progress */}
      <SyncTablesModal
        project={projectName}
        open={showSyncTables}
        onClose={() => { setShowSyncTables(false); setSyncIntro(undefined); void refreshResolvedDb() }}
        intro={syncIntro}
        targetSummary={resolvedDb?.summary}
      />
    </div>
  )
}
